import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Slide, Theme, AspectRatio, SlideElement, ThemeFont, SlideBorderStyle, SlideLayoutType } from '../types';
import { SlideRenderer } from './SlideRenderer';
import { BackgroundController } from './BackgroundController';
import { SlideTemplateSelector } from './SlideTemplateSelector';
import { indexedDBService } from '../utils/indexedDB';
import { SlideTheme, DividerType } from './SlideEditingPanel';
import { SlideEditingPanel } from './SlideEditingPanel';
import { ASPECT_RATIOS } from '../constants';
import { TypingEffect } from './TypingEffect';
import { Toast } from './Toast';
import { SlidePromptPanel } from './SlidePromptPanel';
import { LayerPanel } from './LayerPanel';
import { TRANSLATIONS } from '../constants';
import { generateSlideHTML, createStructuredElements } from '../utils/slideGenerator';
import { 
  LayoutOption, 
  getDefaultLayoutOption, 
  countContentObjects, 
  getContentArea, 
  getBackgroundArea,
  getTitlePositionInBackground,
  calculateObjectPositions, 
  getLayoutOptionsForObjectCount
  // applyDynamicLayout // [주석 처리] 하드코딩된 값 사용으로 인해 사용하지 않음
} from '../utils/dynamicLayout';
import { generatePicsumImage } from '../utils/imageSearch';
import { regenerateSlideContentWithType } from '../utils/geminiApi';
import { regenerateSlideContent } from '../utils/slideRegenerator';
import { useSlideScaling } from '../hooks/useSlideScaling';
import { splitPositionDebouncer } from '../utils/dynamicLayoutRandomizer';
import { getThemeColors } from '../utils/themeColorUtils';
import {
  Plus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Trash2,
  Upload,
  Download,
  RotateCw,
  Square,
  Circle,
  Triangle,
  Star,
  HelpCircle,
  X,
  Keyboard,
  ChevronDown,
  Type,
  MessageSquare,
  BarChart3
} from 'lucide-react';


interface SlidesContainerProps {
  slides: Slide[];
  activeSlideId: string | null;
  activeTab: 'preview' | 'code';
  language: 'ko' | 'en';
  theme: Theme;
  aspectRatio: AspectRatio;
  themeFont?: ThemeFont;
  slideBorderStyle?: SlideBorderStyle;
  isDarkMode?: boolean;
  geminiApiKey?: string;
  onTabChange: (tab: 'preview' | 'code') => void;
  onSlideSelect: (slideId: string) => void;
  onSlideDelete: (slideId: string) => void;
  onAddSlide: (afterIndex?: number) => void;
  onSlideUpdate: (slide: Slide) => void;
  onSlideMove: (fromIndex: number, toIndex: number) => void;
  onThemeChange?: (theme: Theme) => void;
  onThemeFontChange?: (font: ThemeFont) => void;
  onSlideBorderStyleChange?: (style: SlideBorderStyle) => void;
  onRightPanelToggle?: () => void; // 새로 추가
  isRightPanelOpen?: boolean; // 우측 패널 열림 상태
}

export const SlidesContainer: React.FC<SlidesContainerProps> = ({
  slides,
  activeSlideId,
  activeTab,
  language,
  theme,
  aspectRatio,
  themeFont,
  slideBorderStyle,
  isDarkMode = false,
  geminiApiKey,
  onTabChange,
  onSlideSelect,
  onSlideDelete,
  onAddSlide,
  onSlideUpdate,
  onSlideMove,
  onThemeChange,
  onThemeFontChange,
  onSlideBorderStyleChange,
  onRightPanelToggle, // 새로 추가
  isRightPanelOpen = false, // 새로 추가
}) => {
  const [selectedElementId, setSelectedElementId] = useState<string | undefined>();
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [editingElement, setEditingElement] = useState<string | null>(null);
  const [tempSlide, setTempSlide] = useState<Slide | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTypingEffect, setShowTypingEffect] = useState(false);
  const [slidesDisplayCount, setSlidesDisplayCount] = useState(0);
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const [showShapeSelector, setShowShapeSelector] = useState(false);
  const [showTextTypeSelector, setShowTextTypeSelector] = useState(false);
  const [pendingElement, setPendingElement] = useState<{ type: 'text' | 'image' | 'shape' | 'chart' | 'list'; shapeType?: string; textType?: 'title' | 'content' } | null>(null);

  const [showToast, setShowToast] = useState<{ message: string, type: 'info' | 'success' | 'error' } | null>(null);
  const [showLayerPanel, setShowLayerPanel] = useState(false);
  const [copiedElements, setCopiedElements] = useState<SlideElement[]>([]);
  const [editingPanelActiveSection, setEditingPanelActiveSection] = useState<'elements' | 'theme' | 'background'>('theme');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPixabaySearch, setShowPixabaySearch] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<string | null>(null);
  const [showKeyboardHints, setShowKeyboardHints] = useState(true);
  const [hideKeyboardHintsPermanently, setHideKeyboardHintsPermanently] = useState(false);
  
  // 표시 크기 조절 상태 (50% ~ 150%, 기본 100%)
  const [displayScale, setDisplayScale] = useState(100);
  // 동적 레이아웃 상태
  const [selectedLayoutOption, setSelectedLayoutOption] = useState<LayoutOption | undefined>();
  // 레이아웃 간격 설정 ('none' | 'narrow' | 'normal' | 'wide')
  const [layoutSpacing, setLayoutSpacing] = useState<'none' | 'narrow' | 'normal' | 'wide'>('normal');
  const [includeTitleInLayout, setIncludeTitleInLayout] = useState(false); // 제목 객체 포함 여부
  
  // 속성창 위치 상태 (슬라이드 편집창 기준)
  const [propertyPanelPosition, setPropertyPanelPosition] = useState({ x: 400, y: 80 });
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const slideEditorRef = useRef<HTMLDivElement>(null);
  
  // 편집창 섹션 접기 상태
  const [collapsedSections, setCollapsedSections] = useState<{[key: string]: boolean}>({});
  
  // 배경 이미지 셔플용 기준 시드
  const [shuffleSeed, setShuffleSeed] = useState(1);
  const [isShuffling, setIsShuffling] = useState(false);
  
  // 배경 이미지 검색 방식 설정 ('picsum' | 'pixabay')
  const [backgroundSearchMethod, setBackgroundSearchMethod] = useState<'picsum' | 'pixabay'>('picsum');

  const toggleSection = (sectionKey: string) => {
    const newState = { ...collapsedSections, [sectionKey]: !collapsedSections[sectionKey] };
    setCollapsedSections(newState);
    // Save to IndexedDB
    indexedDBService.saveUserPreferences({
      collapsedSections: newState,
      propertyPanelPosition,
      hideKeyboardHints: hideKeyboardHintsPermanently,
      displayScale
    }).catch(error => {
      console.warn('Failed to save collapsed sections:', error);
    });
  };

  // Load user preferences from IndexedDB on component mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const preferences = await indexedDBService.loadUserPreferences();
        if (preferences) {
          setCollapsedSections(preferences.collapsedSections);
          setPropertyPanelPosition(preferences.propertyPanelPosition);
          setHideKeyboardHintsPermanently(preferences.hideKeyboardHints);
          setShowKeyboardHints(!preferences.hideKeyboardHints);
          if (preferences.displayScale) {
            setDisplayScale(preferences.displayScale);
          }
        }
      } catch (error) {
        console.warn('Failed to load user preferences:', error);
      }
    };

    loadUserPreferences();
    return undefined;
  }, []);

  // activeSlideId가 변경될 때 tempSlide 초기화
  useEffect(() => {
    if (activeSlideId) {
      const currentSlide = slides.find(s => s.id === activeSlideId);
      if (currentSlide && (!tempSlide || tempSlide.id !== currentSlide.id)) {
        console.log('[SlidesContainer] activeSlideId 변경 - tempSlide 초기화:', currentSlide.id);
        setTempSlide(currentSlide);
      }
    } else {
      setTempSlide(null);
    }
  }, [activeSlideId]); // slides를 dependency에서 제거하여 무한 루프 방지

  // Multi-selection and grouping handlers
  const handleElementSelect = useCallback((elementId: string, shiftKey = false) => {
    if (shiftKey) {
      // Multi-selection with Shift key
      setSelectedElementIds(prev => {
        if (prev.includes(elementId)) {
          // Remove from selection
          return prev.filter(id => id !== elementId);
        } else {
          // Add to selection
          return [...prev, elementId];
        }
      });
      // Keep the single selection for backward compatibility
      if (!selectedElementIds.includes(elementId)) {
        setSelectedElementId(elementId);
      }
    } else {
      // Single selection
      setSelectedElementId(elementId);
      setSelectedElementIds([elementId]);
    }
  }, [selectedElementIds]);

  const createGroup = useCallback(() => {
    if (!tempSlide || selectedElementIds.length < 2) {return;}
    
    const groupId = `group-${Date.now()}`;
    const groupName = `Group ${(tempSlide.groups?.length || 0) + 1}`;
    
    // Calculate group position as center of selected elements
    const selectedElements = tempSlide.elements?.filter(el => selectedElementIds.includes(el.id)) || [];
    const avgX = selectedElements.reduce((sum, el) => sum + (el.x || 0), 0) / selectedElements.length;
    const avgY = selectedElements.reduce((sum, el) => sum + (el.y || 0), 0) / selectedElements.length;
    
    // Update elements with group info and relative positions
    const updatedElements = tempSlide.elements?.map(el => {
      if (selectedElementIds.includes(el.id)) {
        return {
          ...el,
          groupId,
          groupPosition: {
            x: (el.x || 0) - avgX,
            y: (el.y || 0) - avgY
          }
        };
      }
      return el;
    }) || [];

    // Create the group
    const newGroup = {
      id: groupId,
      name: groupName,
      elementIds: selectedElementIds,
      position: { x: avgX, y: avgY }
    };

    setTempSlide(prev => prev ? {
      ...prev,
      elements: updatedElements,
      groups: [...(prev.groups || []), newGroup]
    } : null);

    setHasUnsavedChanges(true);
    setSelectedElementIds([]);
    setSelectedElementId(undefined);
  }, [tempSlide, selectedElementIds]);

  const ungroupElements = useCallback((groupId: string) => {
    if (!tempSlide) {return;}

    const group = tempSlide.groups?.find(g => g.id === groupId);
    if (!group) {return;}

    // Remove group info from elements and restore absolute positions
    const updatedElements = tempSlide.elements?.map(el => {
      if (el.groupId === groupId) {
        const { groupId: _, groupPosition, ...elementWithoutGroup } = el;
        return {
          ...elementWithoutGroup,
          x: group.position.x + (groupPosition?.x || 0),
          y: group.position.y + (groupPosition?.y || 0)
        };
      }
      return el;
    }) || [];

    setTempSlide(prev => prev ? {
      ...prev,
      elements: updatedElements,
      groups: prev.groups?.filter(g => g.id !== groupId) || []
    } : null);

    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleBackgroundImageChange = useCallback((background: string) => {
    if (!tempSlide) {return;}
    
    // Determine if it's a color or image based on the format
    const isColor = background.startsWith('#') || 
                    background.startsWith('rgb') || 
                    background.startsWith('linear-gradient') ||
                    background.startsWith('radial-gradient');
    
    if (isColor) {
      const updatedSlide = {
        ...tempSlide,
        backgroundColor: background,
        backgroundType: 'color' as const
      };
      setTempSlide(updatedSlide);
    } else {
      // 이미지 URL에서 시드 추출
      let seedValue = tempSlide.backgroundSeed || '';
      if (background.includes('picsum.photos/seed/')) {
        const seedMatch = background.match(/seed\/([^/]+)\//); 
        if (seedMatch) {
          seedValue = seedMatch[1];
        }
      }
      
      const updatedSlide = {
        ...tempSlide,
        backgroundImage: background,
        backgroundType: 'image' as const,
        ...(seedValue && { backgroundSeed: seedValue })
      };
      setTempSlide(updatedSlide);
    }
    
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleAnimatedBackgroundChange = useCallback((effect: 'birds' | 'fog' | 'waves' | 'clouds' | 'clouds2' | 'globe' | 'net' | 'cells' | 'trunk' | 'topology' | 'dots' | 'rings' | 'halo', options: { backgroundColor?: string; color1?: string; color2?: string; scale?: number; speed?: number }) => {
    if (!tempSlide) {return;}
    const updatedSlide = { 
      ...tempSlide, 
      animatedBackgroundEffect: effect,
      animatedBackgroundOptions: options,
      backgroundType: 'animated' as const
    };
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleSeedChange = useCallback((seed: string) => {
    if (!tempSlide) {return;}
    const updatedSlide = { ...tempSlide, backgroundSeed: seed };
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleBackgroundTypeChange = useCallback((type: 'image' | 'color' | 'animated') => {
    if (!tempSlide) {return;}
    const updatedSlide = { ...tempSlide, backgroundType: type };
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleAnimatedEffectChange = useCallback((effect: string) => {
    if (!tempSlide) {return;}
    const updatedSlide = { ...tempSlide, animatedBackgroundEffect: effect as 'birds' | 'fog' | 'waves' | 'clouds' | 'clouds2' | 'globe' | 'net' | 'cells' | 'trunk' | 'topology' | 'dots' | 'rings' | 'halo' };
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const handleAnimatedOptionsChange = useCallback((options: { backgroundColor?: string; color1?: string; color2?: string; scale?: number; speed?: number }) => {
    if (!tempSlide) {return;}
    const updatedSlide = { ...tempSlide, animatedBackgroundOptions: options };
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  }, [tempSlide]);

  const containerRef = useRef<HTMLDivElement>(null);
  
  // 제목 요소 판별 공통 함수 - 타입 기반으로만 판별
  const isTitleElement = useCallback((element: SlideElement): boolean => {
    // element.type === 'title'인 경우
    if (element.type === 'title') {return true;}
    
    // text 타입이면서 제목으로 명시적으로 설정된 경우
    if (element.type === 'text' && element.textType === 'title') {return true;}
    
    // 기존 제목 요소들을 위한 호환성 (id로 판별)
    if (element.type === 'text' && element.id && element.id.includes('title')) {return true;}
    
    // 위치나 크기로 판단하지 않음 - 제목은 명시적으로만 판단
    return false;
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollToRef = useRef<HTMLDivElement>(null);

  const t = TRANSLATIONS[language];

  // 선택된 슬라이드가 변경될 때 스크롤
  useEffect(() => {
    if (activeSlideId && scrollToRef.current) {
      // 슬라이드 헤더가 화면 최상단에 오도록 스크롤
      setTimeout(() => {
        if (scrollToRef.current && scrollToRef.current.getBoundingClientRect) {
          try {
            const mainContentArea = document.getElementById('main-content-area');
            if (mainContentArea) {
              const rect = scrollToRef.current.getBoundingClientRect();
              const containerRect = mainContentArea.getBoundingClientRect();
              const scrollTop = mainContentArea.scrollTop;
              const targetScroll = scrollTop + rect.top - containerRect.top;
              
              if (typeof targetScroll === 'number' && !isNaN(targetScroll) && targetScroll >= 0) {
                // Custom faster scroll animation
                const startScroll = mainContentArea.scrollTop;
                const distance = targetScroll - startScroll;
                const duration = 250; // 250ms for 2x speed (default smooth is ~500ms)
                const startTime = performance.now();
                
                const animateScroll = (currentTime: number) => {
                  const elapsed = currentTime - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  
                  // Easing function for smooth animation
                  const easeInOutQuad = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
                  
                  mainContentArea.scrollTop = startScroll + distance * easeInOutQuad(progress);
                  
                  if (progress < 1) {
                    requestAnimationFrame(animateScroll);
                  }
                };
                
                requestAnimationFrame(animateScroll);
              }
            }
          } catch (error) {
            console.error('Error scrolling to slide:', error);
          }
        }
      }, 100); // 렌더링 완료 후 스크롤
    }
  }, [activeSlideId]);

  // 현재 슬라이드 인덱스 계산
  const currentSlideIndex = activeSlideId ? slides.findIndex(s => s.id === activeSlideId) : -1;
  const currentSlide = currentSlideIndex >= 0 ? slides[currentSlideIndex] : null;
  

  // Use the unified scaling hook - moved here to be available for handleSlideClick
  const editModeScaling = useSlideScaling({
    aspectRatio,
    containerRef,
    isEditMode: true,
    displayScale,
    useActualChildSize: false, // 브라우저 크기 기반으로 계산
    isRightPanelOpen // 우측 패널 열림 여부 전달
  });

  // 미리보기 모드도 편집 모드와 동일한 크기 사용

  // 새 슬라이드가 추가될 때 타이핑 효과 트리거
  useEffect(() => {
    if (slides.length > slidesDisplayCount) {
      setShowTypingEffect(true);
      setSlidesDisplayCount(slides.length);
      
      // 타이핑 효과 완료 후 자동으로 비활성화
      const timer = setTimeout(() => {
        setShowTypingEffect(false);
      }, slides.length * 2000 + 3000); // 슬라이드 개수에 따라 시간 조정

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [slides.length, slidesDisplayCount]);

  // 자동 저장 함수를 useCallback으로 안정화
  const saveSlide = useCallback(() => {
    if (!tempSlide) {return;}
    
    console.log('saveSlide called for:', tempSlide.id, 'slideTheme:', tempSlide.slideTheme);
    
    // 동적 레이아웃 정보 저장
    const layoutOptions = getLayoutOptionsForObjectCount(countContentObjects(tempSlide.elements || [], aspectRatio.height, includeTitleInLayout));
    const selectedIndex = selectedLayoutOption ? layoutOptions.findIndex(opt => opt.type === selectedLayoutOption.type) : -1;
    
    const updatedSlide = {
      ...tempSlide,
      elements: tempSlide.elements || [],
      dynamicLayout: {
        ...tempSlide.dynamicLayout,
        selectedLayoutIndex: selectedIndex,
        availableLayouts: layoutOptions
      }
    };
    
    console.log('Calling onSlideUpdate with slide:', updatedSlide.id, 'slideTheme:', updatedSlide.slideTheme);
    onSlideUpdate(updatedSlide);
    setHasUnsavedChanges(false);
    console.log('Slide saved successfully, hasUnsavedChanges set to false');
  }, [tempSlide, onSlideUpdate, selectedLayoutOption, aspectRatio]);

  // 슬라이드 변경 시 이전 슬라이드 저장
  const prevActiveSlideIdRef = useRef<string | null>(null);
  
  // slides를 ref로 관리하여 useEffect dependency 문제 해결
  const slidesRef = useRef(slides);
  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);
  
  useEffect(() => {
    // 이전 슬라이드가 있고 변경사항이 있으면 저장
    if (prevActiveSlideIdRef.current && tempSlide && hasUnsavedChanges && prevActiveSlideIdRef.current !== activeSlideId) {
      // console.log('Saving previous slide before switching:', prevActiveSlideIdRef.current);
      saveSlide();
    }
    prevActiveSlideIdRef.current = activeSlideId;
    return undefined;
  }, [activeSlideId, tempSlide, hasUnsavedChanges, saveSlide]);
  
  // 컴포넌트 언마운트 시 저장되지 않은 변경사항 저장
  useEffect(() => {
    return () => {
      if (tempSlide && hasUnsavedChanges) {
        saveSlide();
      }
    };
  }, [tempSlide, hasUnsavedChanges, saveSlide]);

  // 슬라이드 선택 시 임시 슬라이드 설정
  useEffect(() => {
    if (activeSlideId) {
      // ref를 통해 최신 slides 참조
      const slide = slidesRef.current.find(s => s.id === activeSlideId);
      if (slide) {
        // 이미 동일한 슬라이드의 tempSlide가 있고 변경사항이 있으면 건드리지 않음
        if (tempSlide && tempSlide.id === slide.id && hasUnsavedChanges) {
          // 아무것도 하지 않음 - 사용자의 변경사항 유지
          return;
        }
        
        // 이미 동일한 슬라이드가 tempSlide에 있고, slideTheme이 다른 경우
        // 사용자가 변경 중인 경우에만 유지
        if (tempSlide && tempSlide.id === slide.id && tempSlide.slideTheme !== slide.slideTheme && hasUnsavedChanges) {
          console.log('[슬라이드 선택] 테마 변경 중, tempSlide 유지:', tempSlide.slideTheme, 'vs', slide.slideTheme);
          // 변경사항이 있을 때만 tempSlide의 테마를 유지
          return;
        }
        
        // 새로운 슬라이드를 선택했거나 변경사항이 없는 경우에만 초기화
        if (!tempSlide || tempSlide.id !== slide.id) {
          // Issue #3 fix: Preserve all background properties when re-entering edit mode
          setTempSlide({ 
            ...slide,
            // Ensure all background properties are preserved
            backgroundImage: slide.backgroundImage,
            backgroundSeed: slide.backgroundSeed,
            backgroundBlur: slide.backgroundBlur,
            backgroundGrayscale: slide.backgroundGrayscale,
            backgroundType: slide.backgroundType,
            backgroundColor: slide.backgroundColor,
            backgroundPattern: slide.backgroundPattern,
            themeOverlay: slide.themeOverlay,
            animatedBackgroundEffect: slide.animatedBackgroundEffect,
            animatedBackgroundOptions: slide.animatedBackgroundOptions,
            // Ensure slide theme is preserved
            slideTheme: slide.slideTheme,
            splitPosition: slide.splitPosition,
            // Ensure elements array is preserved
            elements: slide.elements || []
          });
          setHasUnsavedChanges(false);
          
          // Load saved layout option if exists, otherwise keep current
          const objectCount = countContentObjects(slide.elements || [], aspectRatio.height, includeTitleInLayout);
          const layoutOptions = getLayoutOptionsForObjectCount(objectCount);
          
          // 현재 슬라이드에 동적 레이아웃이 설정되어 있다면 로드
          if (slide.dynamicLayout?.selectedLayoutIndex !== undefined && layoutOptions.length > 0) {
            const selectedIndex = Math.min(slide.dynamicLayout.selectedLayoutIndex, layoutOptions.length - 1);
            const savedLayout = layoutOptions[selectedIndex];
            if (savedLayout) {
              setSelectedLayoutOption(savedLayout);
            }
          } else {
            // 슬라이드가 변경될 때마다 레이아웃 선택 상태를 초기화하고 기본값 설정
            if (layoutOptions.length > 0) {
              setSelectedLayoutOption(layoutOptions[0]);
            } else {
              setSelectedLayoutOption(undefined);
            }
          }
        }
      }
    } else {
      setTempSlide(null);
      setSelectedElementId(undefined);
      setEditingElement(null);
    }
  }, [activeSlideId, tempSlide?.id, hasUnsavedChanges, aspectRatio.height, includeTitleInLayout]); // slides dependency 제거

  // 외부 props 변경 시 tempSlide 강제 업데이트
  useEffect(() => {
    if (tempSlide) {
      // 글로벌 폰트나 테두리 스타일 변경 시에만 업데이트
      // 슬라이드 테마는 사용자가 직접 변경할 때만 업데이트되어야 함
      if (themeFont || slideBorderStyle) {
        setTempSlide(prev => prev ? {
          ...prev,
          themeFont: themeFont,
        } : null);
        setHasUnsavedChanges(true);
      }
    }
  }, [tempSlide?.id, themeFont, slideBorderStyle]);

  // 슬라이드 테마 변경 시에도 레이아웃은 사용자가 명시적으로 변경할 때만 재적용되어야 함

  // 이전 테마를 저장하기 위한 ref
  const prevThemeRef = useRef(theme);
  
  // 테마 색상 변경 시 기본 색상을 사용하는 텍스트 요소만 업데이트
  // 중요: 사용자가 직접 설정한 색상과 폰트는 보존해야 함
  useEffect(() => {
    if (tempSlide && tempSlide.elements) {
      const prevTheme = prevThemeRef.current;
      
      // 테마가 실제로 변경되었는지 확인
      if (JSON.stringify(prevTheme) === JSON.stringify(theme)) {
        return;
      }
      
      // 테마 색상이 변경되면 이전 테마 색상을 사용하던 요소들만 업데이트
      const updatedElements = tempSlide.elements.map(element => {
        if (element.type === 'text') {
          // 이전 테마 색상을 사용하고 있었는지 확인
          const wasUsingPrevThemeColor = element.color === prevTheme.primary || 
                                        element.color === prevTheme.secondary ||
                                        element.color === prevTheme.accent;
          
          if (wasUsingPrevThemeColor) {
            // 이전 색상에 따라 새 색상 결정
            let newColor = element.color;
            if (element.color === prevTheme.primary) {newColor = theme.primary;}
            else if (element.color === prevTheme.secondary) {newColor = theme.secondary;}
            else if (element.color === prevTheme.accent) {newColor = theme.accent;}
            
            return {
              ...element,
              color: newColor,
              backgroundColor: element.backgroundColor === (prevTheme.secondary + '20') ? theme.secondary + '20' : element.backgroundColor
            } as SlideElement;
          }
        }
        return element;
      });
      
      // 변경된 요소가 있는 경우에만 업데이트
      const hasChanges = updatedElements.some((elem, index) => 
        JSON.stringify(elem) !== JSON.stringify(tempSlide.elements![index])
      );
      
      if (hasChanges) {
        // 업데이트된 요소로 tempSlide 업데이트 (slideTheme과 다른 설정은 보존)
        setTempSlide(prev => prev ? {
          ...prev,
          elements: updatedElements
        } : null);
        setHasUnsavedChanges(true);
      }
      
      // 현재 테마를 저장
      prevThemeRef.current = theme;
    }
  }, [theme]);

  // HTML 업데이트 함수 - 필요한 시점에만 호출
  const updateSlideHtml = useCallback(() => {
    if (!tempSlide) {return;}

    // 배경 타입에 따라 표시할 배경 결정
    const finalBackgroundImage = (tempSlide.backgroundType === 'image' || !tempSlide.backgroundType)
      ? (tempSlide.backgroundSeed
          ? generatePicsumImage(
              aspectRatio.width,
              aspectRatio.height,
              tempSlide.backgroundSeed,
              tempSlide.backgroundBlur || 2,
              tempSlide.backgroundGrayscale || false
            )
          : tempSlide.backgroundImage)
      : undefined;

    // Include title and content positions as custom elements if they exist
    const allElements = [...(tempSlide.elements || [])];
    
    // Add title position as a custom element if it exists
    if (tempSlide.titlePosition) {
      allElements.push({
        id: 'default-title',
        type: 'text' as const,
        content: tempSlide.title,
        x: tempSlide.titlePosition.x,
        y: tempSlide.titlePosition.y,
        width: tempSlide.titlePosition.width,
        height: tempSlide.titlePosition.height,
        fontSize: tempSlide.titlePosition.fontSize || 24,
        fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
        color: tempSlide.backgroundImage ? '#FFFFFF' : theme.primary,
        fontWeight: themeFont?.effects.fontWeight || '600',
        textAlign: 'center' as const,
        zIndex: 10,
      });
    }
    
    // Add content position as a custom element if it exists
    if (tempSlide.contentPosition) {
      allElements.push({
        id: 'default-content',
        type: 'text' as const,
        content: tempSlide.content,
        x: tempSlide.contentPosition.x,
        y: tempSlide.contentPosition.y,
        width: tempSlide.contentPosition.width,
        height: tempSlide.contentPosition.height,
        fontSize: tempSlide.contentPosition.fontSize || 16,
        fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
        color: tempSlide.backgroundImage ? '#F5F5F5' : theme.secondary,
        fontWeight: '400',
        textAlign: 'center' as const,
        zIndex: 9,
      });
    }

    const updatedHtmlContent = generateSlideHTML(
      tempSlide.titlePosition ? '' : tempSlide.title, // Don't pass title if we have custom position
      tempSlide.contentPosition ? '' : tempSlide.content, // Don't pass content if we have custom position
      theme,
      aspectRatio,
      tempSlide.order,
      themeFont,
      slideBorderStyle,
      tempSlide.slideLayout || tempSlide.template || 'title-top-content-bottom',
      finalBackgroundImage,
      tempSlide.backgroundBlur || 2,
      tempSlide.themeOverlay || 0.3,
      tempSlide.backgroundType === 'color' ? tempSlide.backgroundColor : undefined,
      tempSlide.backgroundPattern,
      allElements,
      'ppt', // 기본값
      false, // fontBorder
      false, // fontGlow
      tempSlide.slideTheme || 'default',
      tempSlide.dividerType || 'straight'
    );

    // HTML이 실제로 변경되었을 때만 업데이트
    // 메모리 누수 방지를 위해 현재 HTML과 비교
    setTempSlide(prev => {
      if (!prev) {return null;}
      
      // HTML이 동일하면 업데이트하지 않음
      if (prev.htmlContent === updatedHtmlContent && 
          prev.backgroundImage === finalBackgroundImage) {
        return prev;
      }
      
      // 변경사항이 있을 때만 업데이트
      setHasUnsavedChanges(true);
      
      return {
        ...prev,
        backgroundImage: finalBackgroundImage,
        htmlContent: updatedHtmlContent,
      };
    });
  }, [
    tempSlide?.id,
    tempSlide?.title,
    tempSlide?.content,
    tempSlide?.backgroundImage,
    tempSlide?.backgroundType,
    tempSlide?.backgroundColor,
    tempSlide?.backgroundPattern,
    tempSlide?.backgroundBlur,
    tempSlide?.backgroundGrayscale,
    tempSlide?.backgroundSeed,
    tempSlide?.animatedBackgroundEffect,
    tempSlide?.animatedBackgroundOptions,
    tempSlide?.titlePosition,
    tempSlide?.contentPosition,
    // elements의 깊은 비교를 위해 JSON.stringify 사용
    JSON.stringify(tempSlide?.elements),
    tempSlide?.slideLayout,
    tempSlide?.template,
    tempSlide?.slideTheme,
    tempSlide?.dividerType,
    theme,
    aspectRatio,
    themeFont,
    slideBorderStyle
  ]);

  // tempSlide가 변경되거나 의존성이 변경될 때 HTML 업데이트
  // debounce를 200ms로 설정하여 반응성과 성능의 균형 유지
  useEffect(() => {
    if (tempSlide && tempSlide.id) {
      // 적절한 지연으로 연속적인 업데이트를 방지하면서 반응성 유지
      const timeoutId = setTimeout(() => {
        updateSlideHtml();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
    // 조건을 만족하지 않아도 cleanup 함수 반환
    return undefined;
  }, [updateSlideHtml]);

  // 자동 저장 - 변경사항을 실제 슬라이드에 저장
  useEffect(() => {
    if (!tempSlide || !hasUnsavedChanges) {
      console.log('Auto-save skipped:', { tempSlide: !!tempSlide, hasUnsavedChanges });
      return;
    }

    console.log('Auto-save triggered for slide:', tempSlide.id, 'slideTheme:', tempSlide.slideTheme);
    const timer = setTimeout(() => {
      console.log('Auto-save executing for slide:', tempSlide.id);
      saveSlide();
    }, 2000); // 2초로 늘려서 테마 변경과 레이아웃 적용이 완료될 시간 확보

    return () => clearTimeout(timer);
  }, [tempSlide, hasUnsavedChanges, saveSlide]);


  // 실시간 IndexedDB 자동 저장
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (slides.length > 0) {
        indexedDBService.saveDraft(slides).catch(error => {
          console.error('Auto-save failed:', error);
        });
      }
    }, 1000); // 1초 후 자동 저장

    return () => clearTimeout(autoSaveTimer);
  }, [slides]);

  // IndexedDB 초기화
  useEffect(() => {
    indexedDBService.initDB().catch(error => {
      console.error('Failed to initialize IndexedDB:', error);
    });
    return undefined;
  }, []);

  // 규격 4번: 제목 포함 옵션 변경 시 제목 위치 및 레이아웃 처리
  useEffect(() => {
    if (!tempSlide) {return;}
    
    const handleTitleOptionChange = async () => {
      console.log('[제목 옵션] 변경 감지:', includeTitleInLayout ? '포함' : '제외');
      
      // 제목 요소들 찾기
      const titleElements = tempSlide.elements?.filter(element => isTitleElement(element)) || [];
      
      if (titleElements.length === 0) {
        console.log('[제목 옵션] 제목 요소가 없음');
        return;
      }
      
      // 업데이트된 요소들
      const updatedElements = tempSlide.elements?.map(element => {
        const isTitle = isTitleElement(element);
        
        if (isTitle) {
          if (includeTitleInLayout) {
            // 규격: 제목 옵션 체크 시 - 콘텐츠 영역으로 이동 (레이아웃에서 처리)
            console.log('[제목 옵션] 제목을 레이아웃에 포함');
            return element; // 위치는 레이아웃에서 결정
          } else {
            // 규격: 제목 옵션 체크 해제 시 - 테마별 배경 위치로 이동
            console.log('[제목 옵션] 제목을 배경 위치로 이동');
            const backgroundArea = getBackgroundArea(aspectRatio, tempSlide.slideTheme || 'default', tempSlide.splitPosition || 30);
            const titlePosition = getTitlePositionInBackground(backgroundArea, tempSlide.slideTheme || 'default', aspectRatio);
            
            return {
              ...element,
              x: titlePosition.x,
              y: titlePosition.y,
              width: titlePosition.width,
              height: titlePosition.height,
              fontSize: titlePosition.fontSize
            };
          }
        }
        
        return element;
      }) || [];
      
      // 상태 업데이트
      const updatedSlide = {
        ...tempSlide,
        elements: updatedElements
      };
      
      setTempSlide(updatedSlide);
      setHasUnsavedChanges(true);
      
      // 규격: 제목 옵션 체크 시 즉시 동적 레이아웃 1회 적용
      if (includeTitleInLayout && selectedLayoutOption) {
        // 안정화 대기
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('[제목 옵션] 제목 포함으로 동적 레이아웃 즉시 적용');
        applyLayout(selectedLayoutOption);
        
        await new Promise(resolve => setTimeout(resolve, 50));
        console.log('[제목 옵션] 레이아웃 적용 완료');
      }
    };
    
    // 비동기 실행
    handleTitleOptionChange().catch(error => {
      console.error('[제목 옵션] 처리 실패:', error);
    });
    
  }, [includeTitleInLayout, tempSlide?.id]); // tempSlide.id로 슬라이드 변경 시에만 실행

  // 속성창 드래그 핸들러
  const handlePanelMouseDown = (e: React.MouseEvent) => {
    if (!slideEditorRef.current) {return;}
    
    const editorRect = slideEditorRef.current.getBoundingClientRect();
    const relativeX = e.clientX - editorRect.left;
    const relativeY = e.clientY - editorRect.top;
    
    setIsDraggingPanel(true);
    setDragOffset({
      x: relativeX - propertyPanelPosition.x,
      y: relativeY - propertyPanelPosition.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingPanel || !slideEditorRef.current) {return;}
      
      const editorRect = slideEditorRef.current.getBoundingClientRect();
      const relativeX = e.clientX - editorRect.left;
      const relativeY = e.clientY - editorRect.top;
      
      const newX = relativeX - dragOffset.x;
      const newY = relativeY - dragOffset.y;
      
      // 슬라이드 편집창 경계 체크
      const panelWidth = 320;
      const panelHeight = 400;
      const maxX = editorRect.width - panelWidth;
      const maxY = editorRect.height - panelHeight;
      
      const constrainedPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      };
      
      setPropertyPanelPosition(constrainedPosition);
    };

    const handleMouseUp = () => {
      if (isDraggingPanel) {
        setIsDraggingPanel(false);
        // 위치 저장 to IndexedDB
        indexedDBService.saveUserPreferences({
          collapsedSections,
          propertyPanelPosition,
          hideKeyboardHints: hideKeyboardHintsPermanently
        }).catch(error => {
          console.warn('Failed to save property panel position:', error);
        });
      }
    };

    if (isDraggingPanel) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingPanel, dragOffset, propertyPanelPosition]);

  // Copy/Paste keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tempSlide || !activeSlideId) {return;}
      
      // Handle copy (Ctrl+C) - 단일 또는 다중 선택 지원
      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        
        if (selectedElementIds.length > 0) {
          // 다중 선택된 요소들 복사
          const elementsToCopy = tempSlide.elements?.filter(el => selectedElementIds.includes(el.id)) || [];
          if (elementsToCopy.length > 0) {
            setCopiedElements(elementsToCopy);
            setShowToast({ message: `${elementsToCopy.length}개 요소가 복사되었습니다`, type: 'success' });
          }
        } else if (selectedElementId) {
          // 단일 선택된 요소 복사
          const elementToCopy = tempSlide.elements?.find(el => el.id === selectedElementId);
          if (elementToCopy) {
            setCopiedElements([elementToCopy]);
            setShowToast({ message: t.copied || '복사되었습니다', type: 'success' });
          }
        }
      }
      
      // Handle paste (Ctrl+V)
      if (e.ctrlKey && e.key === 'v' && copiedElements.length > 0) {
        e.preventDefault();
        const containerSize = editModeScaling.actualWidth ? 
          { width: editModeScaling.actualWidth, height: editModeScaling.actualHeight } : 
          { width: aspectRatio.width, height: aspectRatio.height };
        
        const timestamp = Date.now();
        const newElements: SlideElement[] = [];
        const newElementIds: string[] = [];
        
        copiedElements.forEach((copiedElement, index) => {
          const isRatioBased = copiedElement.x < 1 && copiedElement.y < 1;
          const offset = isRatioBased ? 0.02 : containerSize.width * 0.02;
          
          const newElement: SlideElement = {
            ...copiedElement,
            id: `element-${timestamp}-${index}`,
            x: isRatioBased ? 
              Math.min(copiedElement.x + 0.02, 1 - copiedElement.width) : 
              Math.min(copiedElement.x + offset, containerSize.width - copiedElement.width),
            y: isRatioBased ? 
              Math.min(copiedElement.y + 0.02, 1 - copiedElement.height) : 
              Math.min(copiedElement.y + offset, containerSize.height - copiedElement.height),
            zIndex: (tempSlide.elements?.length || 0) + index + 1
          };
          
          newElements.push(newElement);
          newElementIds.push(newElement.id);
        });
        
        setTempSlide(prev => prev ? {
          ...prev,
          elements: [...(prev.elements || []), ...newElements]
        } : null);
        setHasUnsavedChanges(true);
        
        // 다중 요소가 붙여넣어진 경우 모두 선택
        if (newElements.length > 1) {
          setSelectedElementIds(newElementIds);
          setSelectedElementId(undefined);
        } else {
          setSelectedElementId(newElements[0].id);
          setSelectedElementIds([]);
        }
        
        setShowToast({ 
          message: copiedElements.length > 1 ? 
            `${copiedElements.length}개 요소가 붙여넣기되었습니다` : 
            (t.pasted || '붙여넣기되었습니다'), 
          type: 'success' 
        });
      }
      
      // Handle delete (Delete key) - 단일 또는 다중 선택 지원
      if (e.key === 'Delete') {
        e.preventDefault();
        
        if (selectedElementIds.length > 0) {
          // 다중 선택된 요소들 삭제
          setTempSlide(prev => prev ? {
            ...prev,
            elements: (prev.elements || []).filter(el => !selectedElementIds.includes(el.id))
          } : null);
          setSelectedElementIds([]);
          setHasUnsavedChanges(true);
          setShowToast({ message: `${selectedElementIds.length}개 요소가 삭제되었습니다`, type: 'success' });
        } else if (selectedElementId) {
          // 단일 선택된 요소 삭제 (기존 확인 다이얼로그 사용)
          setElementToDelete(selectedElementId);
          setShowDeleteConfirm(true);
        }
      }
      
      // Handle Tab key for layer navigation
      if (e.key === 'Tab' && tempSlide?.elements && tempSlide.elements.length > 0) {
        e.preventDefault();
        
        // Sort elements by zIndex (layer order)
        const sortedElements = [...tempSlide.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
        
        if (sortedElements.length === 0) {return;}
        
        // Find current selected element index
        const currentIndex = selectedElementId 
          ? sortedElements.findIndex(el => el.id === selectedElementId)
          : -1;
        
        let nextIndex;
        if (e.shiftKey) {
          // Shift+Tab: Navigate backwards
          nextIndex = currentIndex > 0 ? currentIndex - 1 : sortedElements.length - 1;
        } else {
          // Tab: Navigate forwards
          nextIndex = currentIndex < sortedElements.length - 1 ? currentIndex + 1 : 0;
        }
        
        const nextElement = sortedElements[nextIndex];
        if (nextElement) {
          setSelectedElementId(nextElement.id);
          setSelectedElementIds([nextElement.id]);
          setShowToast({ 
            message: `${language === 'ko' ? '선택됨' : 'Selected'}: ${
              nextElement.type === 'text' ? (language === 'ko' ? '텍스트' : 'Text') :
              nextElement.type === 'image' ? (language === 'ko' ? '이미지' : 'Image') :
              nextElement.type === 'shape' ? (language === 'ko' ? '도형' : 'Shape') :
              nextElement.type
            } (Layer ${nextElement.zIndex || 0})`, 
            type: 'info' 
          });
        }
      }
      
      // Handle arrow keys for moving elements - 단일 또는 다중 선택 지원
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const hasSelection = selectedElementId || selectedElementIds.length > 0;
        // 텍스트 편집 중이 아닐 때만 요소 이동
        if (hasSelection && !editingElement) {
          e.preventDefault();
          
          const moveDistance = e.shiftKey ? 0.01 : 0.005; // Shift로 더 크게 이동
          
          let deltaX = 0;
          let deltaY = 0;
          
          switch (e.key) {
            case 'ArrowLeft':
              deltaX = -moveDistance;
              break;
            case 'ArrowRight':
              deltaX = moveDistance;
              break;
            case 'ArrowUp':
              deltaY = -moveDistance;
              break;
            case 'ArrowDown':
              deltaY = moveDistance;
              break;
          }
          
          if (selectedElementIds.length > 0) {
            // 다중 선택된 요소들 모두 이동
            setTempSlide(prev => prev ? {
              ...prev,
              elements: (prev.elements || []).map(el => 
                selectedElementIds.includes(el.id) ? {
                  ...el,
                  x: Math.max(0, Math.min(1 - el.width, el.x + deltaX)),
                  y: Math.max(0, Math.min(1 - el.height, el.y + deltaY))
                } : el
              )
            } : null);
          } else if (selectedElementId) {
            // 단일 선택된 요소 이동
            setTempSlide(prev => prev ? {
              ...prev,
              elements: (prev.elements || []).map(el => 
                el.id === selectedElementId ? {
                  ...el,
                  x: Math.max(0, Math.min(1 - el.width, el.x + deltaX)),
                  y: Math.max(0, Math.min(1 - el.height, el.y + deltaY))
                } : el
              )
            } : null);
          }
          
          setHasUnsavedChanges(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, selectedElementIds, copiedElements, tempSlide, activeSlideId, editModeScaling, aspectRatio, t, language]);

  // includeTitleInLayout 변경 시 즉시 제목 요소 재분류 및 동적 레이아웃 재실행
  useEffect(() => {
    if (!tempSlide || !tempSlide.elements) {return;}
    
    // 제목 요소들을 분리
    const titleElements = tempSlide.elements.filter(element => isTitleElement(element));
    const nonTitleElements = tempSlide.elements.filter(element => !isTitleElement(element));
    
    let updatedElements = [...tempSlide.elements];
    
    if (!includeTitleInLayout && titleElements.length > 0) {
      // includeTitleInLayout이 false인 경우 제목을 배경 영역에 배치
      const updatedTitleElements = titleElements.map(titleElement => {
        if (tempSlide.slideTheme && tempSlide.slideTheme !== 'default') {
          // 테마별 배경 영역 계산
          const backgroundArea = getBackgroundArea(aspectRatio, tempSlide.slideTheme, tempSlide.splitPosition || 25);
          const bgTitlePosition = getTitlePositionInBackground(backgroundArea, tempSlide.slideTheme, aspectRatio);
          
          return {
            ...titleElement,
            x: bgTitlePosition.x,
            y: bgTitlePosition.y,
            width: bgTitlePosition.width,
            height: bgTitlePosition.height,
            fontSize: bgTitlePosition.fontSize
          };
        } else {
          // 기본 테마일 경우 상단 중앙에 배치
          return {
            ...titleElement,
            x: 0.1,
            y: 0.05,
            width: 0.8,
            height: 0.1,
            fontSize: titleElement.fontSize && titleElement.fontSize < 32 ? 32 : titleElement.fontSize
          };
        }
      });
      
      // 제목 요소들을 업데이트된 위치로 교체
      updatedElements = tempSlide.elements.map(element => {
        const updatedTitle = updatedTitleElements.find(t => t.id === element.id);
        return updatedTitle || element;
      });
    }
    
    const updatedSlide = {
      ...tempSlide,
      elements: updatedElements
    };
    
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
    
    // 동적 레이아웃 재실행 - selectedLayoutOption이 있고 요소가 변경된 경우에만
    if (selectedLayoutOption && JSON.stringify(updatedElements) !== JSON.stringify(tempSlide.elements)) {
      // 약간의 지연 후 레이아웃 적용 (상태 업데이트 이후)
      setTimeout(() => {
        applyLayout(selectedLayoutOption);
      }, 50);
    }
  }, [includeTitleInLayout]); // selectedLayoutOption, slideTheme, splitPosition 제거하여 무한 루프 방지

  // 요소 업데이트 함수 - HTML 직접 업데이트 포함
  // HTML 재생성 공통 함수 - 메모이제이션 적용으로 메모리 누수 방지
  const regenerateHtmlContent = useCallback((slide: Slide, updatedElements?: SlideElement[], preventAutoGenerate?: boolean) => {
    const elements = updatedElements || slide.elements || [];
    
    // Include title and content positions as custom elements if they exist
    const allElements = [...elements];
    
    // 테마 색상 가져오기
    const themeColors = getThemeColors(theme);
    
    // 자동 생성 방지 플래그가 설정되지 않은 경우에만 default 요소 추가
    if (!preventAutoGenerate) {
      // Add title position as a custom element if it exists and not already in elements
      if (slide.titlePosition && !elements.some(el => el.id === 'default-title')) {
        allElements.push({
          id: 'default-title',
          type: 'text' as const,
          content: slide.title,
          x: slide.titlePosition.x,
          y: slide.titlePosition.y,
          width: slide.titlePosition.width,
          height: slide.titlePosition.height,
          fontSize: slide.titlePosition.fontSize || 24,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: slide.backgroundImage ? '#FFFFFF' : themeColors.font,
          fontWeight: themeFont?.effects.fontWeight || '600',
          textAlign: 'center' as const,
          zIndex: 10,
        });
      }
      
      // Add content position as a custom element if it exists and not already in elements
      if (slide.contentPosition && !elements.some(el => el.id === 'default-content')) {
        allElements.push({
          id: 'default-content',
          type: 'text' as const,
          content: slide.content,
          x: slide.contentPosition.x,
          y: slide.contentPosition.y,
          width: slide.contentPosition.width,
          height: slide.contentPosition.height,
          fontSize: slide.contentPosition.fontSize || 16,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: slide.backgroundImage ? '#F5F5F5' : themeColors.font,
          fontWeight: '400',
          textAlign: 'center' as const,
          zIndex: 9,
        });
      }
    }

    // HTML 콘텐츠 재생성
    return generateSlideHTML(
      slide.titlePosition ? '' : slide.title,
      slide.contentPosition ? '' : slide.content,
      theme,
      aspectRatio,
      slide.order,
      themeFont,
      slideBorderStyle,
      slide.slideLayout || slide.template || 'title-top-content-bottom',
      slide.backgroundImage,
      slide.backgroundBlur || 2,
      slide.themeOverlay || 0.3,
      slide.backgroundImage ? undefined : slide.backgroundColor,
      slide.backgroundPattern,
      allElements,
      'ppt',
      slide.fontBorder,
      slide.fontGlow,
      slide.slideTheme || 'default',
      slide.dividerType || 'straight'
    );
  }, [aspectRatio, theme, themeFont, slideBorderStyle, generateSlideHTML]);

  const updateElement = useCallback((elementId: string, updates: Partial<SlideElement>) => {
    if (!tempSlide) {return;}

    const updatedElements = tempSlide.elements?.map(el =>
      el.id === elementId ? { ...el, ...updates } : el
    ) || [];

    const updatedSlide = { ...tempSlide, elements: updatedElements };
    const updatedHtmlContent = regenerateHtmlContent(updatedSlide, updatedElements);

    setTempSlide({
      ...updatedSlide,
      htmlContent: updatedHtmlContent,
    });
    setHasUnsavedChanges(true);
  }, [tempSlide, regenerateHtmlContent]);

  // 요소 삭제 함수
  const deleteElement = useCallback((elementId: string) => {
    if (!tempSlide) {return;}

    // 삭제할 요소 찾기
    const elementToDelete = tempSlide.elements?.find(el => el.id === elementId);
    
    // 이미지/비디오 요소인 경우 blob URL 해제
    if (elementToDelete && (elementToDelete.type === 'image' || elementToDelete.type === 'video')) {
      if (elementToDelete.content && elementToDelete.content.startsWith('blob:')) {
        console.log(`${elementToDelete.type} blob URL 해제:`, elementToDelete.content);
        URL.revokeObjectURL(elementToDelete.content);
      }
    }

    const updatedElements = tempSlide.elements?.filter(el => el.id !== elementId) || [];
    let updatedSlide = { ...tempSlide, elements: updatedElements };
    
    
    // default-title 또는 default-content 삭제 시 해당 position도 제거
    if (elementId === 'default-title') {
      updatedSlide = { ...updatedSlide, titlePosition: undefined };
    } else if (elementId === 'default-content') {
      updatedSlide = { ...updatedSlide, contentPosition: undefined };
    }
    
    // 텍스트 요소 삭제 시 자동 재생성 방지
    const isTextElement = tempSlide.elements?.find(el => el.id === elementId)?.type === 'text';
    const updatedHtmlContent = regenerateHtmlContent(updatedSlide, updatedElements, isTextElement);

    setTempSlide({
      ...updatedSlide,
      htmlContent: updatedHtmlContent,
    });
    
    // 삭제된 요소가 선택되어 있었다면 선택 해제
    if (selectedElementId === elementId) {
      setSelectedElementId(undefined);
    }
    
    // 삭제된 요소가 편집 중이었다면 편집 상태 해제
    if (editingElement === elementId) {
      setEditingElement(null);
    }
    
    setHasUnsavedChanges(true);
    setShowDeleteConfirm(false);
    setElementToDelete(null);
    
    // 요소 삭제 후 자동으로 레이아웃 적용
    setTimeout(() => {
      const contentElements = updatedElements.filter(el => {
        if (el.type === 'title') {return false;}
        if (el.type === 'shape') {return false;}
        if (el.type === 'text' && el.content && el.format) {return true;}
        if (el.type === 'text' && el.y && el.y <= 0.10 && el.fontSize && el.fontSize > 32) {return false;}
        return true;
      });
      
      const newObjectCount = contentElements.length;
      if (newObjectCount > 0) {
        const layoutOptions = getLayoutOptionsForObjectCount(newObjectCount);
        
        if (layoutOptions.length > 0) {
          // 랜덤으로 레이아웃 선택
          const randomIndex = Math.floor(Math.random() * layoutOptions.length);
          const randomLayout = layoutOptions[randomIndex];
          
          // handleDynamicLayoutChange가 정의되기 전이므로 직접 상태 업데이트
          setSelectedLayoutOption(randomLayout);
        }
      }
    }, 100);
  }, [tempSlide, selectedElementId, editingElement, regenerateHtmlContent]);

  // 삭제 확인 처리
  const handleDeleteConfirm = useCallback(() => {
    if (elementToDelete) {
      deleteElement(elementToDelete);
      setShowToast({ message: t.deleted || '삭제되었습니다', type: 'success' });
    }
  }, [elementToDelete, deleteElement, t]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setElementToDelete(null);
  }, []);

  // 슬라이드 크기에 비례한 객체 크기와 위치 계산
  const getScaledElementDimensions = useCallback((elementType: 'shape' | 'text' | 'image') => {
    // 비율 기반 좌표계 사용 (0-1)
    const baseWidth = 0.1; // 10% 기본 너비
    const baseHeight = 0.1; // 10% 기본 높이
    const baseX = 0.1; // 10% 위치
    const baseY = 0.15; // 15% 위치 (제목 영역 아래)
    
    switch (elementType) {
      case 'image':
        return {
          width: baseWidth * 2, // 20% 너비
          height: baseHeight * 1.5, // 15% 높이
          x: baseX,
          y: baseY
        };
      case 'text':
        return {
          width: baseWidth * 3, // 30% 너비
          height: baseHeight * 0.8, // 8% 높이
          x: baseX,
          y: baseY
        };
      case 'shape':
      default:
        return {
          width: baseWidth, // 10% 너비
          height: baseHeight, // 10% 높이
          x: baseX,
          y: baseY
        };
    }
  }, []);

  // Helper function to add element with grid layout support
  const addElementWithGridSupport = useCallback((element: SlideElement) => {
    console.log('addElementWithGridSupport 시작:', element);
    console.log('tempSlide 존재 여부:', !!tempSlide);
    
    if (!tempSlide) {
      console.log('tempSlide가 없어서 종료');
      return;
    }
    
    setTempSlide(prev => {
      if (!prev) {
        console.log('prev가 null이어서 종료');
        return null;
      }
      
      const updatedElements = [...(prev.elements || []), element];
      console.log('업데이트된 elements 배열:', updatedElements);
      
      // 동적 레이아웃 적용을 위한 콘텐츠 요소 필터링
      const contentElements = updatedElements.filter(el => {
        if (el.type === 'title') {return false;}
        if (el.type === 'shape') {return false;}
        // 텍스트 요소 처리
        if (el.type === 'text' && el.content && el.format) {return true;}
        if (el.type === 'text' && el.y && el.y <= 0.10 && el.fontSize && el.fontSize > 32) {return false;}
        // 이미지와 동영상 요소 명시적으로 포함
        if (el.type === 'image' || el.type === 'video') {return true;}
        // 차트, 리스트 등 다른 콘텐츠 요소들도 포함
        if (el.type === 'chart' || el.type === 'list' || el.type === 'column') {return true;}
        return true;
      });
      
      const newObjectCount = contentElements.length;
      const layoutOptions = getLayoutOptionsForObjectCount(newObjectCount);
      
      let finalElements = updatedElements;
      
      if (layoutOptions.length > 0) {
        // 랜덤으로 레이아웃 선택
        const randomIndex = Math.floor(Math.random() * layoutOptions.length);
        const randomLayout = layoutOptions[randomIndex];
        
        // 콘텐츠 영역 계산 (slideTheme과 splitPosition 고려)
        const contentArea = getContentArea(aspectRatio, prev.slideTheme || 'default', prev.splitPosition || 25);
        
        // layoutSpacing을 숫자로 변환
        const spacingMap = {
          'none': 0,
          'narrow': 0.01,
          'normal': 0.02,
          'wide': 0.03
        };
        const numericSpacing = spacingMap[layoutSpacing] || 0.02;
        
        // 새로운 레이아웃에 따른 위치 계산
        const newPositions = calculateObjectPositions(
          randomLayout, 
          contentElements.length, 
          contentArea, 
          aspectRatio.width, 
          aspectRatio.height,
          numericSpacing
        );
        
        // 요소들의 위치 업데이트
        finalElements = updatedElements.map(elem => {
          // 도형은 건드리지 않음
          if (elem.type === 'shape') {return elem;}
          
          // 제목 판별
          const isTitle = isTitleElement(elem);
          
          // 제목이고 includeTitleInLayout이 false이면 동적 레이아웃에서 제외
          if (isTitle && !includeTitleInLayout) {
            return elem; // 현재 위치 유지
          }
          
          const elementIndex = contentElements.findIndex(el => el.id === elem.id);
          if (elementIndex >= 0 && elementIndex < newPositions.length) {
            const position = newPositions[elementIndex];
            return {
              ...elem,
              x: position.x,
              y: position.y,
              width: position.width,
              height: position.height
            };
          }
          
          return elem;
        });
        
        // selectedLayoutOption 업데이트는 별도로 처리해야 함
        setSelectedLayoutOption(randomLayout);
      }
      
      const updatedHtmlContent = regenerateHtmlContent({ ...prev, elements: finalElements });
      console.log('regenerateHtmlContent 완료');
      
      const result = {
        ...prev,
        elements: finalElements,
        htmlContent: updatedHtmlContent,
      };
      console.log('setTempSlide에 전달할 최종 결과:', result);
      return result;
    });
    
    setHasUnsavedChanges(true);
    setSelectedElementId(element.id);
    console.log('addElementWithGridSupport 완료');
  }, [tempSlide, aspectRatio, layoutSpacing, includeTitleInLayout]);


  // 내용에 맞는 크기 계산 함수
  const calculateContentSize = (content: string, type: 'text' | 'image' | 'shape' | 'chart' | 'list', _fontSize: number) => {
    if (type === 'text' || type === 'chart' || type === 'list') {
      // 텍스트 길이 기반 크기 계산 (비율 기반)
      const textLength = content.length;
      const lineCount = Math.ceil(textLength / 20); // 대략 20자당 1줄
      const baseWidth = Math.max(textLength * 0.008, 0.1); // 최소 10% 너비
      const baseHeight = Math.max(lineCount * 0.02, 0.05); // 최소 5% 높이
      
      return {
        width: Math.min(baseWidth, 0.4), // 최대 40% 너비
        height: Math.min(baseHeight, 0.2), // 최대 20% 높이
      };
    }
    
    // 기본 크기 (비율 기반)
    return {
      width: 0.15, // 15% 너비
      height: 0.12, // 12% 높이
    };
  };

  const addNewElement = (type: 'text' | 'image' | 'shape' | 'chart' | 'list' = 'text', shapeType?: 'rectangle' | 'rounded-rectangle' | 'triangle' | 'circle' | 'star', textType?: 'title' | 'content') => {
    if (!tempSlide) {return;}

    // 콘텐츠 영역 계산 (slideTheme과 splitPosition 고려)
    const contentArea = getContentArea(aspectRatio, tempSlide.slideTheme || 'default', tempSlide.splitPosition || 25);
    
    // textType에 따라 다른 요소 생성
    let elementType = type;
    let content = '';
    const listItems: string[] = [];
    
    if (type === 'text') {
      // AI 슬라이드와 동일한 형식으로 생성
      if (textType === 'content') {
        content = language === 'ko' ? '• 첫 번째 항목\n• 두 번째 항목\n• 세 번째 항목' : '• First item\n• Second item\n• Third item';
      } else if (textType === 'title') {
        content = language === 'ko' ? '새 제목' : 'New Title';
      } else {
        content = t.newText || (language === 'ko' ? '새 텍스트' : 'New Text');
      }
    } else if (type === 'chart') {
      elementType = 'chart';
      content = language === 'ko' ? '새 차트' : 'New Chart';
    }
    
    const fontSize = textType === 'title' ? Math.max(aspectRatio.height * 0.04, 20) : Math.max(aspectRatio.height * 0.025, 14);
    const contentSize = calculateContentSize(content || listItems.join(' '), elementType, fontSize);

    // 위치 계산
    let x, y;
    
    if (textType === 'title') {
      // 제목은 제목 영역에 배치
      x = 0.1;
      y = 0.05;
    } else {
      // 다른 요소들은 콘텐츠 영역에 배치
      const contentX = contentArea.x / aspectRatio.width;
      const contentY = contentArea.y / aspectRatio.height;
      const contentWidth = contentArea.width / aspectRatio.width;
      const contentHeight = contentArea.height / aspectRatio.height;
      
      // 콘텐츠 영역 중앙에 배치
      x = contentX + (contentWidth - contentSize.width) / 2;
      y = contentY + (contentHeight - contentSize.height) / 2;
    }

    const newElement: SlideElement = {
      id: `element-${Date.now()}`,
      type: elementType,
      content,
      ...(elementType === 'list' && { listItems, listType: 'bullet' }),
      // 제목 요소인 경우 textType 속성 추가
      ...(textType === 'title' && { textType: 'title' }),
      x: Math.max(0, Math.min(x, 1 - contentSize.width)),
      y: Math.max(0, Math.min(y, 1 - contentSize.height)),
      width: contentSize.width,
      height: contentSize.height,
      fontSize,
      fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
      color: type === 'shape' ? 'transparent' : (textType === 'title' ? theme.secondary : theme.primary),
      fontWeight: themeFont?.effects?.fontWeight || 'normal',
      textAlign: 'left',
      verticalAlign: 'middle' as const,
      textStroke: themeFont?.effects?.textStroke,
      glowEffect: themeFont?.effects?.glowEffect,
      zIndex: (tempSlide.elements?.length || 0) + 1,
      backgroundColor: type === 'shape' ? theme.primary : (type === 'text' ? theme.secondary + '20' : undefined),
      borderColor: type === 'shape' ? theme.secondary : ((type === 'text' || elementType === 'list') ? theme.primary + '20' : undefined),
      borderWidth: type === 'shape' ? 2 : ((type === 'text' || elementType === 'list') ? 1 : undefined),
      shapeType: type === 'shape' ? shapeType : undefined,
      borderStyle: type === 'shape' ? 'solid' : ((type === 'text' || elementType === 'list') ? 'solid' : undefined),
      backgroundPattern: type === 'shape' ? 'solid' : undefined,
      borderRadius: (type === 'text' || elementType === 'list') ? 8 : (type === 'shape' && shapeType === 'rounded-rectangle' ? 16 : undefined),
      padding: (type === 'text' || elementType === 'list') ? 16 : undefined,
      boxShadow: (type === 'text' || elementType === 'list') ? '0 2px 8px rgba(0,0,0,0.1)' : (type === 'shape' ? '0 4px 12px rgba(0,0,0,0.15)' : undefined),
      // Chart 타입일 때 기본 차트 데이터 추가
      ...(elementType === 'chart' && {
        chartData: {
          type: 'bar',
          data: {
            labels: [language === 'ko' ? '항목1' : 'Item1', language === 'ko' ? '항목2' : 'Item2', language === 'ko' ? '항목3' : 'Item3'],
            values: [30, 50, 20]
          },
          title: language === 'ko' ? '새 차트' : 'New Chart'
        },
        backgroundColor: theme.secondary + '20',
        borderColor: theme.primary + '30',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
      }),
    };

    setTempSlide(prev => {
      if (!prev) {return null;}
      
      const updatedElements = [...(prev.elements || []), newElement];
      
      // 자동 동적 레이아웃 적용 (랜덤 선택)
      const availableLayouts = getLayoutOptionsForObjectCount(updatedElements.length);
      if (availableLayouts.length > 0) {
        const randomLayout = availableLayouts[Math.floor(Math.random() * availableLayouts.length)];
        // [주석 처리] applyDynamicLayout는 하드코딩된 해상도를 사용하므로 제거
        // 대신 이미 위에서 newPositions를 계산했으므로 그것을 사용
        
        // Include title and content positions as custom elements if they exist
        const layoutAppliedSlide = { ...prev, elements: updatedElements };
        const allElements = [...updatedElements];
        
        // Add title position as a custom element if it exists
        if (prev.titlePosition) {
          allElements.push({
            id: 'default-title',
            type: 'text' as const,
            content: prev.title,
            x: prev.titlePosition.x,
            y: prev.titlePosition.y,
            width: prev.titlePosition.width,
            height: prev.titlePosition.height,
            fontSize: prev.titlePosition.fontSize || 24,
            fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
            color: prev.backgroundImage ? '#FFFFFF' : theme.primary,
            fontWeight: themeFont?.effects.fontWeight || '600',
            textAlign: 'center' as const,
            zIndex: 10,
          });
        }
        
        // Add content position as a custom element if it exists
        if (layoutAppliedSlide.contentPosition) {
          allElements.push({
            id: 'default-content',
            type: 'text' as const,
            content: layoutAppliedSlide.content,
            x: layoutAppliedSlide.contentPosition.x,
            y: layoutAppliedSlide.contentPosition.y,
            width: layoutAppliedSlide.contentPosition.width,
            height: layoutAppliedSlide.contentPosition.height,
            fontSize: layoutAppliedSlide.contentPosition.fontSize || 16,
            fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
            color: layoutAppliedSlide.backgroundImage ? '#F5F5F5' : theme.secondary,
            fontWeight: '400',
            textAlign: 'center' as const,
            zIndex: 9,
          });
        }
        
        const layoutHtmlContent = generateSlideHTML(
          layoutAppliedSlide.titlePosition ? '' : layoutAppliedSlide.title,
          layoutAppliedSlide.contentPosition ? '' : layoutAppliedSlide.content,
          theme,
          aspectRatio,
          layoutAppliedSlide.order,
          themeFont,
          slideBorderStyle,
          layoutAppliedSlide.slideLayout || layoutAppliedSlide.template || 'title-top-content-bottom',
          layoutAppliedSlide.backgroundImage,
          layoutAppliedSlide.backgroundBlur || 2,
          layoutAppliedSlide.themeOverlay || 0.3,
          layoutAppliedSlide.backgroundImage ? undefined : layoutAppliedSlide.backgroundColor,
          layoutAppliedSlide.backgroundPattern,
          allElements,
          'ppt',
          layoutAppliedSlide.fontBorder,
          layoutAppliedSlide.fontGlow,
          layoutAppliedSlide.slideTheme || 'default',
          layoutAppliedSlide.dividerType || 'straight'
        );
        
        const finalSlide = {
          ...layoutAppliedSlide,
          htmlContent: layoutHtmlContent,
        };
        // 슬라이드 업데이트 호출
        onSlideUpdate(finalSlide);
        return finalSlide;
      }
      
      // 동적 레이아웃이 없는 경우
      // Include title and content positions as custom elements if they exist
      const allElements = [...updatedElements];
      
      // Add title position as a custom element if it exists
      if (prev.titlePosition) {
        allElements.push({
          id: 'default-title',
          type: 'text' as const,
          content: prev.title,
          x: prev.titlePosition.x,
          y: prev.titlePosition.y,
          width: prev.titlePosition.width,
          height: prev.titlePosition.height,
          fontSize: prev.titlePosition.fontSize || 24,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: prev.backgroundImage ? '#FFFFFF' : theme.primary,
          fontWeight: themeFont?.effects.fontWeight || '600',
          textAlign: 'center' as const,
          zIndex: 10,
        });
      }
      
      // Add content position as a custom element if it exists
      if (prev.contentPosition) {
        allElements.push({
          id: 'default-content',
          type: 'text' as const,
          content: prev.content,
          x: prev.contentPosition.x,
          y: prev.contentPosition.y,
          width: prev.contentPosition.width,
          height: prev.contentPosition.height,
          fontSize: prev.contentPosition.fontSize || 16,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: prev.backgroundImage ? '#F5F5F5' : theme.secondary,
          fontWeight: '400',
          textAlign: 'center' as const,
          zIndex: 9,
        });
      }

      // HTML 콘텐츠 재생성
      const updatedHtmlContent = generateSlideHTML(
        prev.titlePosition ? '' : prev.title,
        prev.contentPosition ? '' : prev.content,
        theme,
        aspectRatio,
        prev.order,
        themeFont,
        slideBorderStyle,
        prev.slideLayout || prev.template || 'title-top-content-bottom',
        prev.backgroundImage,
        prev.backgroundBlur || 2,
        prev.themeOverlay || 0.3,
        prev.backgroundImage ? undefined : prev.backgroundColor,
        prev.backgroundPattern,
        allElements,
        'ppt',
        prev.fontBorder,
        prev.fontGlow,
        prev.slideTheme || 'default',
        prev.dividerType || 'straight'
      );

      const finalSlide = {
        ...prev,
        elements: updatedElements,
        htmlContent: updatedHtmlContent,
      };
      // 슬라이드 업데이트 호출
      onSlideUpdate(finalSlide);
      return finalSlide;
    });
    setHasUnsavedChanges(true);
    setSelectedElementId(newElement.id);
    setShowToast({ 
      message: language === 'ko' ? '요소가 추가되었습니다' : 'Element added successfully',
      type: 'success'
    });
  };

  const handleSlideClick = useCallback((e: React.MouseEvent) => {
    if (!pendingElement || !tempSlide || !containerRef.current) {return;}

    const rect = containerRef.current.getBoundingClientRect();
    const scale = editModeScaling.scale;
    
    // 실제 슬라이드 크기 (aspectRatio 기반)
    const slideWidth = aspectRatio.width;
    const slideHeight = aspectRatio.height;
    
    // 클릭 위치를 슬라이드 좌표로 변환
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    // 슬라이드 내부인지 확인
    if (x < 0 || x > slideWidth || y < 0 || y > slideHeight) {
      setShowToast({ 
        message: language === 'ko' ? '슬라이드 내부를 클릭하세요' : 'Click inside the slide',
        type: 'error'
      });
      return;
    }
    
    // 비율로 변환 (0-1)
    const xRatio = x / slideWidth;
    const yRatio = y / slideHeight;

    const { type, shapeType, textType } = pendingElement;
    
    // textType에 따라 다른 요소 생성
    let elementType = type;
    let content = '';
    const listItems: string[] = [];
    
    if (type === 'text') {
      // AI 슬라이드와 동일한 형식으로 생성
      if (textType === 'content') {
        content = language === 'ko' ? '• 첫 번째 항목\n• 두 번째 항목\n• 세 번째 항목' : '• First item\n• Second item\n• Third item';
      } else if (textType === 'title') {
        content = language === 'ko' ? '새 제목' : 'New Title';
      } else {
        content = t.newText || (language === 'ko' ? '새 텍스트' : 'New Text');
      }
    } else if (type === 'chart') {
      elementType = 'chart';
      content = language === 'ko' ? '새 차트' : 'New Chart';
    }
    
    const fontSize = textType === 'title' ? Math.max(aspectRatio.height * 0.04, 20) : Math.max(aspectRatio.height * 0.025, 14);
    const contentSize = calculateContentSize(content || listItems.join(' '), elementType, fontSize);

    const newElement: SlideElement = {
      id: `element-${Date.now()}`,
      type: elementType,
      content,
      ...(elementType === 'list' && { listItems, listType: 'bullet' }),
      x: Math.max(0, Math.min(xRatio - (contentSize.width / 2), 1 - contentSize.width)),
      y: Math.max(0, Math.min(yRatio - (contentSize.height / 2), 1 - contentSize.height)),
      width: contentSize.width,
      height: contentSize.height,
      fontSize, // 반응형 폰트 크기
      fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
      color: type === 'shape' ? 'transparent' : (textType === 'title' ? theme.secondary : theme.primary),
      fontWeight: themeFont?.effects?.fontWeight || 'normal',
      textAlign: 'left',
      verticalAlign: 'middle' as const,
      // 폰트 효과 적용
      textStroke: themeFont?.effects?.textStroke,
      glowEffect: themeFont?.effects?.glowEffect,
      zIndex: (tempSlide.elements?.length || 0) + 1,
      backgroundColor: type === 'shape' ? theme.primary : (type === 'text' ? theme.secondary + '20' : undefined),
      borderColor: type === 'shape' ? theme.secondary : ((type === 'text' || elementType === 'list') ? theme.primary + '20' : undefined),
      borderWidth: type === 'shape' ? 2 : ((type === 'text' || elementType === 'list') ? 1 : undefined),
      shapeType: type === 'shape' ? shapeType : undefined,
      borderStyle: type === 'shape' ? 'solid' : ((type === 'text' || elementType === 'list') ? 'solid' : undefined),
      backgroundPattern: type === 'shape' ? 'solid' : undefined,
      borderRadius: (type === 'text' || elementType === 'list') ? 8 : (type === 'shape' && shapeType === 'rounded-rectangle' ? 16 : undefined),
      padding: (type === 'text' || elementType === 'list') ? 16 : undefined,
      boxShadow: (type === 'text' || elementType === 'list') ? '0 2px 8px rgba(0,0,0,0.1)' : (type === 'shape' ? '0 4px 12px rgba(0,0,0,0.15)' : undefined),
      // Chart 타입일 때 기본 차트 데이터 추가
      ...(elementType === 'chart' && {
        chartData: {
          type: 'bar',
          data: {
            labels: [language === 'ko' ? '항목1' : 'Item1', language === 'ko' ? '항목2' : 'Item2', language === 'ko' ? '항목3' : 'Item3'],
            values: [30, 50, 20]
          },
          title: language === 'ko' ? '새 차트' : 'New Chart'
        },
        backgroundColor: theme.secondary + '20',
        borderColor: theme.primary + '30',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
      }),
    };

    setTempSlide(prev => {
      if (!prev) {return null;}
      
      const updatedElements = [...(prev.elements || []), newElement];
      
      
      // Include title and content positions as custom elements if they exist
      const allElements = [...updatedElements];
      
      // Add title position as a custom element if it exists
      if (prev.titlePosition) {
        allElements.push({
          id: 'default-title',
          type: 'text' as const,
          content: prev.title,
          x: prev.titlePosition.x,
          y: prev.titlePosition.y,
          width: prev.titlePosition.width,
          height: prev.titlePosition.height,
          fontSize: prev.titlePosition.fontSize || 24,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: prev.backgroundImage ? '#FFFFFF' : theme.primary,
          fontWeight: themeFont?.effects.fontWeight || '600',
          textAlign: 'center' as const,
          zIndex: 10,
        });
      }
      
      // Add content position as a custom element if it exists
      if (prev.contentPosition) {
        allElements.push({
          id: 'default-content',
          type: 'text' as const,
          content: prev.content,
          x: prev.contentPosition.x,
          y: prev.contentPosition.y,
          width: prev.contentPosition.width,
          height: prev.contentPosition.height,
          fontSize: prev.contentPosition.fontSize || 16,
          fontFamily: themeFont?.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
          color: prev.backgroundImage ? '#F5F5F5' : theme.secondary,
          fontWeight: '400',
          textAlign: 'center' as const,
          zIndex: 9,
        });
      }

      // HTML 콘텐츠 재생성
      const updatedHtmlContent = generateSlideHTML(
        prev.titlePosition ? '' : prev.title,
        prev.contentPosition ? '' : prev.content,
        theme,
        aspectRatio,
        prev.order,
        themeFont,
        slideBorderStyle,
        prev.slideLayout || prev.template || 'title-top-content-bottom',
        prev.backgroundImage,
        prev.backgroundBlur || 2,
        prev.themeOverlay || 0.3,
        prev.backgroundImage ? undefined : prev.backgroundColor,
        prev.backgroundPattern,
        allElements,
        'ppt',
        prev.fontBorder,
        prev.fontGlow,
        prev.slideTheme || 'default',
        prev.dividerType || 'straight'
      );
      
      // 자동 동적 레이아웃 적용 (랜덤 선택)
      const availableLayouts = getLayoutOptionsForObjectCount(updatedElements.length);
      if (availableLayouts.length > 0) {
        const randomLayout = availableLayouts[Math.floor(Math.random() * availableLayouts.length)];
        // [주석 처리] applyDynamicLayout 대신 직접 사용
        const layoutAppliedSlide = prev;
        const layoutHtmlContent = generateSlideHTML(
          layoutAppliedSlide.titlePosition ? '' : layoutAppliedSlide.title,
          layoutAppliedSlide.contentPosition ? '' : layoutAppliedSlide.content,
          theme,
          aspectRatio,
          layoutAppliedSlide.order,
          themeFont,
          slideBorderStyle,
          layoutAppliedSlide.slideLayout || layoutAppliedSlide.template || 'title-top-content-bottom',
          layoutAppliedSlide.backgroundImage,
          layoutAppliedSlide.backgroundBlur || 2,
          layoutAppliedSlide.themeOverlay || 0.3,
          layoutAppliedSlide.backgroundImage ? undefined : layoutAppliedSlide.backgroundColor,
          layoutAppliedSlide.backgroundPattern,
          layoutAppliedSlide.elements || [],
          'ppt',
          layoutAppliedSlide.fontBorder,
          layoutAppliedSlide.fontGlow,
          layoutAppliedSlide.slideTheme || 'default',
          layoutAppliedSlide.dividerType || 'straight'
        );
        return {
          ...layoutAppliedSlide,
          htmlContent: layoutHtmlContent,
        };
      }

      return {
        ...prev,
        elements: updatedElements,
        htmlContent: updatedHtmlContent,
      };
    });
    setHasUnsavedChanges(true);
    setSelectedElementId(newElement.id);
    setPendingElement(null);
  }, [pendingElement, tempSlide, containerRef, editModeScaling.scale, aspectRatio, t, language]);

  const handleImageUpload = useCallback((elementId?: string) => {
    console.log('handleImageUpload 호출됨', { 
      fileInputRef: fileInputRef.current,
      elementId,
      tempSlide: tempSlide?.id,
      tempSlideExists: !!tempSlide,
      activeSlideId,
      slides: slides.map(s => s.id)
    });
    if (fileInputRef.current) {
      console.log('fileInputRef.current 존재함, 파일 선택 대화상자 열기');
      fileInputRef.current.accept = 'image/*';
      fileInputRef.current.onchange = (e) => {
        console.log('파일 선택됨', e);
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          // URL.createObjectURL로 이미지 참조 생성 (BASE64 대신)
          const imageUrl = URL.createObjectURL(file);
          console.log('이미지 URL 생성됨:', imageUrl);

          if (elementId) {
            // 기존 요소 업데이트
            console.log('기존 요소 업데이트', elementId);
            updateElement(elementId, { content: imageUrl });
          } else {
            // 새 이미지 요소 직접 생성 (슬라이드 중앙에 배치)
            console.log('새 이미지 요소 생성 시작');
            const newElement: SlideElement = {
              id: `element-${Date.now()}`,
              type: 'image',
              content: imageUrl,
              x: 0.3, // 30% 위치
              y: 0.3, // 30% 위치
              width: 0.4, // 40% 크기
              height: 0.3, // 30% 크기
              fontSize: 16,
              fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
              color: '#000000',
              fontWeight: 'normal',
              textAlign: 'center',
              zIndex: (tempSlide?.elements?.length || 0) + 1,
            };

            console.log('새 이미지 요소 생성:', newElement);
            console.log('현재 tempSlide:', tempSlide);

            setTempSlide(prev => {
              console.log('setTempSlide 호출됨 - prev:', prev);
              if (!prev) {
                console.error('tempSlide가 null - 이미지 추가 불가');
                return null;
              }
              const updatedElements = [...(prev.elements || []), newElement];
              console.log('업데이트된 요소들:', updatedElements);
              const updatedSlide = {
                ...prev,
                elements: updatedElements,
              };
              // HTML 재생성이 필요한지 확인
              const updatedHtmlContent = regenerateHtmlContent(updatedSlide);
              console.log('HTML 재생성됨');
              return {
                ...updatedSlide,
                htmlContent: updatedHtmlContent
              };
            });
            setHasUnsavedChanges(true);
            setSelectedElementId(newElement.id);
            console.log('이미지 요소 추가 완료');
          }
        }
      };
      console.log('파일 input 클릭 시도');
      fileInputRef.current.click();
    } else {
      console.error('fileInputRef.current가 없음 - 이미지 업로드 불가', { ref: fileInputRef });
    }
  }, [tempSlide, themeFont, updateElement, regenerateHtmlContent, activeSlideId, slides, setSelectedElementId, setHasUnsavedChanges]);

  // 동영상 업로드 함수
  const handleVideoUpload = useCallback((elementId?: string) => {
    console.log('handleVideoUpload 호출됨');
    if (fileInputRef.current) {
      fileInputRef.current.accept = 'video/*';
      fileInputRef.current.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          // URL.createObjectURL로 동영상 참조 생성 (BASE64 대신)
          const videoUrl = URL.createObjectURL(file);
          console.log('비디오 URL 생성됨:', videoUrl);

            if (elementId) {
              // 기존 요소 업데이트
              updateElement(elementId, { content: videoUrl });
            } else {
              // 새 동영상 요소 직접 생성 (슬라이드 중앙에 배치)
              const newElement: SlideElement = {
                id: `element-${Date.now()}`,
                type: 'video',
                content: videoUrl,
                x: 0.3, // 30% 위치
                y: 0.3, // 30% 위치
                width: 0.4, // 40% 크기
                height: 0.3, // 30% 크기
                fontSize: 16,
                fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                color: '#000000',
                fontWeight: 'normal',
                textAlign: 'center',
                zIndex: (tempSlide?.elements?.length || 0) + 1,
              };

              console.log('새 동영상 요소 생성:', newElement);
              console.log('현재 tempSlide:', tempSlide);

              setTempSlide(prev => {
                console.log('setTempSlide 호출됨 - prev:', prev);
                if (!prev) {
                  console.error('tempSlide가 null - 동영상 추가 불가');
                  return null;
                }
                const updatedElements = [...(prev.elements || []), newElement];
                console.log('업데이트된 요소들:', updatedElements);
                const updatedSlide = {
                  ...prev,
                  elements: updatedElements,
                };
                // HTML 재생성이 필요한지 확인
                const updatedHtmlContent = regenerateHtmlContent(updatedSlide);
                console.log('HTML 재생성됨');
                return {
                  ...updatedSlide,
                  htmlContent: updatedHtmlContent
                };
              });
              setHasUnsavedChanges(true);
              setSelectedElementId(newElement.id);
              console.log('동영상 요소 추가 완료');
            }
        }
      };
      console.log('파일 input 클릭 시도');
      fileInputRef.current.click();
    } else {
      console.error('fileInputRef.current가 없음 - 비디오 업로드 불가', { ref: fileInputRef });
    }
  }, [tempSlide, themeFont, updateElement, regenerateHtmlContent, activeSlideId, slides, setSelectedElementId, setHasUnsavedChanges]);

  // 배경 이미지 업로드 함수
  const handleBackgroundImageUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file && tempSlide) {
          const blobUrl = URL.createObjectURL(file);

          // 배경 이미지 설정 (자동 블러 효과 적용)
          const updatedSlide = {
            ...tempSlide,
            backgroundImage: blobUrl,
            backgroundBlur: 2, // 자동으로 블러 효과 적용
            backgroundSeed: undefined, // picsum 시드 제거
            backgroundGrayscale: false, // 그레이스케일 비활성화
            backgroundType: 'image' as const // 이미지 타입으로 설정
          };

          setTempSlide(updatedSlide);
          setHasUnsavedChanges(true);
        }
      };
      fileInputRef.current.click();
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));

    if (imageFile) {
      const blobUrl = URL.createObjectURL(imageFile);

      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        // Use the unified scale from the hook
        const scaleX = editModeScaling.scale;
        const scaleY = editModeScaling.scale;
        
        const x = e.dataTransfer.dropEffect === 'copy' ?
          Math.max(0, ((e.clientX - rect.left - 75) / scaleX) / aspectRatio.width) : 0.1;
        const y = e.dataTransfer.dropEffect === 'copy' ?
          Math.max(0, ((e.clientY - rect.top - 50) / scaleY) / aspectRatio.height) : 0.1;

        const newElement: SlideElement = {
          id: `element-${Date.now()}`,
          type: 'image',
          content: blobUrl,
          x,
          y,
          width: 0.15, // 15% 너비
          height: 0.1, // 10% 높이
          fontSize: 16,
          fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
          color: '#000000',
          fontWeight: 'normal',
          textAlign: 'left',
          zIndex: (tempSlide?.elements?.length || 0) + 1,
        };

        setTempSlide(prev => prev ? {
          ...prev,
          elements: [...(prev.elements || []), newElement],
        } : null);
        setHasUnsavedChanges(true);
        setSelectedElementId(newElement.id);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };


  // 배경 이미지 업데이트 함수들
  const handleBackgroundSeedChange = (seed: string) => {
    if (!tempSlide) {return;}

    const newBackgroundImage = generatePicsumImage(
      aspectRatio.width,
      aspectRatio.height,
      seed,
      tempSlide.backgroundBlur || 2,
      tempSlide.backgroundGrayscale || false
    );

    const updatedSlide = {
      ...tempSlide,
      backgroundSeed: seed,
      backgroundImage: newBackgroundImage,
      // 이미지가 선택되면 이미지 타입으로 설정
      backgroundType: 'image'
    };

    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  const handleBackgroundBlurChange = (blur: number) => {
    if (!tempSlide) {return;}

    const updatedSlide = {
      ...tempSlide,
      backgroundBlur: blur,
    };

    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  const handleBackgroundGrayscaleChange = (grayscale: boolean) => {
    if (!tempSlide) {return;}

    const updatedSlide = {
      ...tempSlide,
      backgroundGrayscale: grayscale,
    };

    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  // 자동 배경 검색 함수 (슬라이드 키워드 기반)
  const handleAutoBackgroundSearch = async () => {
    if (!tempSlide) {return;}
    
    if (backgroundSearchMethod === 'pixabay') {
      // Pixabay API를 사용하여 슬라이드 키워드로 검색
      const keywords = tempSlide.keywords || [];
      if (keywords.length > 0) {
        // 키워드가 있으면 자동으로 검색 모달 열기
        setShowPixabaySearch(true);
      } else {
        // 키워드가 없으면 수동 검색 모달 열기
        setShowPixabaySearch(true);
      }
    } else {
      // Picsum 방식 - 랜덤 이미지 생성
      const randomSeed = Math.floor(Math.random() * 1000) + 1;
      handleBackgroundSeedChange(randomSeed.toString());
    }
  };

  // 표시 크기 조절 함수
  const handleDisplayScaleChange = (newScale: number) => {
    const clampedScale = Math.max(50, Math.min(150, newScale));
    setDisplayScale(clampedScale);
    
    // 설정 저장
    indexedDBService.saveUserPreferences({
      collapsedSections,
      propertyPanelPosition,
      hideKeyboardHints: hideKeyboardHintsPermanently,
      displayScale: clampedScale
    }).catch(error => {
      console.warn('Failed to save display scale:', error);
    });
  };

  // 슬라이드 템플릿 변경 핸들러
  const handleTemplateChange = (newTemplate: SlideLayoutType) => {
    if (!tempSlide) {return;}

    // Get layout positions for the new template
    const getLayoutPositions = () => {
      switch (newTemplate) {
        case 'title-left-content-right':
          return {
            titleX: 0.05,
            titleY: 0.1,
            titleWidth: 0.4,
            titleHeight: 0.8,
            contentX: 0.5,
            contentY: 0.1,
            contentWidth: 0.45,
            contentHeight: 0.8
          };
        case 'title-right-content-left':
          return {
            titleX: 0.55,
            titleY: 0.1,
            titleWidth: 0.4,
            titleHeight: 0.8,
            contentX: 0.05,
            contentY: 0.1,
            contentWidth: 0.45,
            contentHeight: 0.8
          };
        case 'title-center-content-center':
          return {
            titleX: 0.15,
            titleY: 0.05,
            titleWidth: 0.7,
            titleHeight: 0.15,
            contentX: 0.1,
            contentY: 0.25,
            contentWidth: 0.8,
            contentHeight: 0.7
          };
        default: // 'title-top-content-bottom'
          return {
            titleX: 0.15,
            titleY: 0.1,
            titleWidth: 0.7,
            titleHeight: 0.15,
            contentX: 0.05,
            contentY: 0.35,
            contentWidth: 0.9,
            contentHeight: 0.55
          };
      }
    };

    const positions = getLayoutPositions();
    
    // Update structured elements to match new template
    let updatedElements = tempSlide.elements || [];
    if (updatedElements.length > 0) {
      updatedElements = updatedElements.map(element => {
        // Update generated elements and chart elements
        if (element.id.includes('title-') || element.id.includes('content-') || 
            element.id.includes('list-') || element.id.includes('info-') || 
            element.id.includes('signboard-') || element.id.includes('column-') ||
            element.type === 'chart') {
          // Update title elements
          if (element.id.includes('title-')) {
            return {
              ...element,
              x: positions.titleX,
              y: positions.titleY,
              width: positions.titleWidth,
              height: positions.titleHeight
            };
          }
          // Update content elements based on new layout
          else {
            const contentIndex = updatedElements.filter(el => 
              !el.id.includes('title-') && 
              (el.id.includes('content-') || el.id.includes('list-') || 
               el.id.includes('info-') || el.id.includes('signboard-') || 
               el.id.includes('column-') || el.type === 'chart')
            ).indexOf(element);
            
            const totalContentElements = updatedElements.filter(el => 
              !el.id.includes('title-') && 
              (el.id.includes('content-') || el.id.includes('list-') || 
               el.id.includes('info-') || el.id.includes('signboard-') || 
               el.id.includes('column-') || el.type === 'chart')
            ).length;
            
            const elementHeight = positions.contentHeight / Math.ceil(totalContentElements);
            const yOffset = contentIndex * elementHeight;
            
            return {
              ...element,
              x: positions.contentX,
              y: positions.contentY + yOffset,
              width: positions.contentWidth,
              height: elementHeight - 0.01
            };
          }
        }
        return element;
      });
    }

    // When template changes, reset title and content positions
    const updatedSlide = {
      ...tempSlide,
      slideLayout: newTemplate,
      template: newTemplate,
      titlePosition: undefined, // Reset to use template default
      contentPosition: undefined, // Reset to use template default
      elements: updatedElements,
      htmlContent: generateSlideHTML(
        tempSlide.title,
        tempSlide.content,
        theme,
        aspectRatio,
        tempSlide.order,
        themeFont,
        slideBorderStyle,
        newTemplate,
        tempSlide.backgroundImage,
        tempSlide.backgroundBlur || 2,
        tempSlide.themeOverlay || 0.3,
        tempSlide.backgroundImage ? undefined : (tempSlide.backgroundColor || undefined),
        tempSlide.backgroundPattern,
        updatedElements,
        'ppt', // 기본값
        tempSlide.fontBorder,
        tempSlide.fontGlow,
        tempSlide.slideTheme || 'default',
        tempSlide.dividerType || 'straight'
      ),
    };

    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  // 슬라이드 테마 변경 핸들러
  const handleSlideThemeChange = (newTheme: SlideTheme) => {
    if (!tempSlide) {return;}
    
    // 콘텐츠 영역 계산 (slideTheme과 splitPosition 고려)
    const contentArea = getContentArea(aspectRatio, newTheme, tempSlide?.splitPosition || 25);
    
    // 제목 위치 자동 조정 (includeTitleInLayout이 true일 때만 사용)
    const getTitlePosition = (theme: SlideTheme, splitPosition = 30) => {
      const slideWidth = aspectRatio.width;
      const slideHeight = aspectRatio.height;
      
      switch (theme) {
        case 'default':
        case 'diagonal':
          // 기본형, 사선형: 상단 중앙
          return {
            x: 0.1,
            y: 0.05,
            width: 0.8,
            height: 0.15,
            fontSize: Math.max(slideHeight * 0.04, 20)
          };
        case 'left-split': {
          // 좌측형: 좌측 배경 이미지 영역 중앙
          const leftWidth = (splitPosition / 100) * slideWidth;
          return {
            x: 0.05,
            y: 0.4,
            width: (leftWidth / slideWidth) * 0.8,
            height: 0.2,
            fontSize: Math.max(slideHeight * 0.035, 18)
          };
        }
        case 'right-split': {
          // 우측형: 우측 배경 이미지 영역 중앙
          const rightWidth = ((100 - splitPosition) / 100) * slideWidth;
          const rightX = (splitPosition / 100) * slideWidth;
          return {
            x: (rightX / slideWidth) + 0.05,
            y: 0.4,
            width: (rightWidth / slideWidth) * 0.8,
            height: 0.2,
            fontSize: Math.max(slideHeight * 0.035, 18)
          };
        }
        case 'top-split': {
          // 상단형: 상단 배경 이미지 영역 중앙
          const topHeight = (splitPosition / 100) * slideHeight;
          return {
            x: 0.1,
            y: 0.05,
            width: 0.8,
            height: (topHeight / slideHeight) * 0.8,
            fontSize: Math.max(slideHeight * 0.04, 20)
          };
        }
        case 'bottom-split': {
          // 하단형: 하단 배경 이미지 영역 중앙
          const bottomHeight = ((100 - splitPosition) / 100) * slideHeight;
          const bottomY = (splitPosition / 100) * slideHeight;
          return {
            x: 0.1,
            y: (bottomY / slideHeight) + 0.1,
            width: 0.8,
            height: (bottomHeight / slideHeight) * 0.8,
            fontSize: Math.max(slideHeight * 0.04, 20)
          };
        }
        default:
          return {
            x: 0.1,
            y: 0.05,
            width: 0.8,
            height: 0.15,
            fontSize: Math.max(slideHeight * 0.04, 20)
          };
      }
    };
    
    // 제목 위치 계산
    const titlePosition = getTitlePosition(newTheme, tempSlide.splitPosition);
    
    // 기존 요소들의 위치를 콘텐츠 영역 내로 제한 (제목 객체 제외)
    const constrainedElements = tempSlide.elements?.map(element => {
      // 제목 판별
      const isTitle = isTitleElement(element);
      
      // 제목 객체 처리
      if (isTitle) {
        if (newTheme !== 'default' && !includeTitleInLayout) {
          // 배경 영역 계산
          const backgroundArea = getBackgroundArea(aspectRatio, newTheme, tempSlide?.splitPosition || 25);
          const bgTitlePosition = getTitlePositionInBackground(backgroundArea, newTheme, aspectRatio);
          
          return {
            ...element,
            x: bgTitlePosition.x,
            y: bgTitlePosition.y,
            width: bgTitlePosition.width,
            height: bgTitlePosition.height,
            fontSize: bgTitlePosition.fontSize
          };
        } else if (titlePosition) {
          // 기본 테마이거나 includeTitleInLayout이 true인 경우
          return {
            ...element,
            x: titlePosition.x,
            y: titlePosition.y,
            width: titlePosition.width,
            height: titlePosition.height,
            fontSize: titlePosition.fontSize
          };
        }
      }
      
      // 요소가 콘텐츠 영역을 벗어나는 경우 위치 조정
      const elementX = element.x < 1 ? element.x * aspectRatio.width : element.x;
      const elementY = element.y < 1 ? element.y * aspectRatio.height : element.y;
      const elementWidth = element.width < 1 ? element.width * aspectRatio.width : element.width;
      const elementHeight = element.height < 1 ? element.height * aspectRatio.height : element.height;
      
      let newX = elementX;
      let newY = elementY;
      
      // X 좌표 제한
      if (elementX < contentArea.x) {
        newX = contentArea.x;
      } else if (elementX + elementWidth > contentArea.x + contentArea.width) {
        newX = Math.max(contentArea.x, contentArea.x + contentArea.width - elementWidth);
      }
      
      // Y 좌표 제한
      if (elementY < contentArea.y) {
        newY = contentArea.y;
      } else if (elementY + elementHeight > contentArea.y + contentArea.height) {
        newY = Math.max(contentArea.y, contentArea.y + contentArea.height - elementHeight);
      }
      
      // 비율 기반으로 다시 변환
      const finalX = element.x < 1 ? newX / aspectRatio.width : newX;
      const finalY = element.y < 1 ? newY / aspectRatio.height : newY;
      
      return {
        ...element,
        x: finalX,
        y: finalY
      };
    }) || [];
    
    const updatedSlide = {
      ...tempSlide,
      slideTheme: newTheme,
      elements: constrainedElements
    };
    
    // HTML 재생성은 updateSlideHtml에서 자동으로 처리됨
    console.log('Setting tempSlide with new theme:', newTheme, 'for slide:', updatedSlide.id);
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
    console.log('hasUnsavedChanges set to true after theme change');
    
    // 요소 위치가 변경되었음을 알림
    const movedElementsCount = constrainedElements.filter((elem, index) => {
      const original = tempSlide.elements?.[index];
      return original && (original.x !== elem.x || original.y !== elem.y);
    }).length;
    
    if (movedElementsCount > 0) {
      setShowToast({ 
        message: `테마 변경으로 ${movedElementsCount}개 요소가 콘텐츠 영역으로 이동되었습니다`, 
        type: 'info' 
      });
    }
    
    // 규격 2번: 테마 변경 시 기존 레이아웃 자동 재적용
    // 즉시 레이아웃 적용 (updatedSlide의 elements 사용)
    if (selectedLayoutOption && updatedSlide.elements && updatedSlide.elements.length > 0) {
      console.log('[테마 변경] 동적 레이아웃 즉시 적용 시작');
      
      // 콘텐츠 영역 재계산
      const newContentArea = getContentArea(aspectRatio, newTheme, updatedSlide.splitPosition || 25);
      
      // 레이아웃 적용할 요소들 필터링
      const contentElements = updatedSlide.elements.filter(element => {
        if (element.type === 'shape') {return false;}
        const isTitle = isTitleElement(element);
        if (isTitle && !includeTitleInLayout) {return false;}
        return true;
      });
      
      if (contentElements.length > 0) {
        // 레이아웃 위치 계산
        const positions = calculateObjectPositions(
          selectedLayoutOption,
          contentElements.length,
          newContentArea,
          aspectRatio.width,
          aspectRatio.height,
          layoutSpacing === 'none' ? 0 : layoutSpacing === 'narrow' ? 0.01 : layoutSpacing === 'wide' ? 0.04 : 0.02
        );
        
        // 요소 위치 업데이트
        const layoutUpdatedElements = updatedSlide.elements.map(element => {
          const contentIndex = contentElements.findIndex(el => el.id === element.id);
          if (contentIndex !== -1 && positions[contentIndex]) {
            return {
              ...element,
              x: positions[contentIndex].x,
              y: positions[contentIndex].y,
              width: positions[contentIndex].width,
              height: positions[contentIndex].height
            };
          }
          return element;
        });
        
        // 최종 슬라이드 업데이트
        const finalSlide = {
          ...updatedSlide,
          elements: layoutUpdatedElements
        };
        
        setTempSlide(finalSlide);
        console.log('[테마 변경] 동적 레이아웃 적용 완료');
      }
    }
    
    // 자동 저장은 일반 useEffect(line 816-828)에서 hasUnsavedChanges를 통해 처리됨
  };

  // splitPosition 업데이트 핸들러 (규격 3번: 0.3초 디바운스 처리)
  const handleSplitPositionChange = (newPosition: number) => {
    if (!tempSlide) {return;}
    
    // 즉시 UI 업데이트 (시각적 피드백을 위해)
    const immediateUpdate = {
      ...tempSlide,
      splitPosition: newPosition
    };
    setTempSlide(immediateUpdate);
    
    // 디바운스된 레이아웃 재적용
    const slideId = tempSlide.id;
    splitPositionDebouncer.debounce(
      `splitPosition_${slideId}`,
      () => applySplitPositionChangeWithLayout(newPosition),
      600 // 0.6초로 늘려서 안정성 확보
    );
  };
  
  // 실제 분할 위치 변경 및 레이아웃 재적용 처리
  const applySplitPositionChangeWithLayout = (newPosition: number) => {
    if (!tempSlide) {return;}
    
    console.log('[분할 위치] 디바운스 완료, 레이아웃 재적용 시작:', newPosition);
    
    // 극단적인 splitPosition 경고
    if (newPosition < 15 || newPosition > 85) {
      setShowToast({
        message: language === 'ko' 
          ? '제목 영역이 너무 좊습니다. 가독성이 떨어질 수 있습니다.'
          : 'Title area is too narrow. Readability may be affected.',
        type: 'error'
      });
    }
    
    // 제목 위치 계산 - includeTitleInLayout에 따라 처리
    let titlePositionData = null;
    if (!includeTitleInLayout && tempSlide.slideTheme) {
      // includeTitleInLayout이 false면 배경 영역에 배치
      const backgroundArea = getBackgroundArea(aspectRatio, tempSlide.slideTheme, newPosition);
      titlePositionData = getTitlePositionInBackground(backgroundArea, tempSlide.slideTheme, aspectRatio);
    }
    
    // 제목 위치 업데이트
    const updatedElements = tempSlide.elements?.map(element => {
      // 제목 판별
      const isTitle = isTitleElement(element);
      
      if (isTitle && !includeTitleInLayout && titlePositionData) {
        // includeTitleInLayout이 false일 때만 위치 업데이트
        return {
          ...element,
          x: titlePositionData.x,
          y: titlePositionData.y,
          width: titlePositionData.width,
          height: titlePositionData.height,
          fontSize: titlePositionData.fontSize,
          ...(titlePositionData.backgroundColor && { backgroundColor: titlePositionData.backgroundColor }),
          ...(titlePositionData.textShadow && { textShadow: titlePositionData.textShadow })
        };
      }
      return element;
    }) || [];
    
    // splitPosition과 제목 위치 업데이트
    const updatedSlide = {
      ...tempSlide,
      splitPosition: newPosition,
      elements: updatedElements,
      titlePosition: tempSlide.titlePosition // 제목 위치는 별도로 관리
    };
    
    // 동적 레이아웃이 적용된 경우 요소 재배치
    if (updatedSlide.dynamicLayout && updatedSlide.elements && updatedSlide.elements.length > 0) {
      try {
        // dynamicLayout이 이미 객체이므로 JSON.parse가 필요없음
        const layoutOption = typeof updatedSlide.dynamicLayout === 'string' 
          ? JSON.parse(updatedSlide.dynamicLayout) 
          : updatedSlide.dynamicLayout;
        const newContentArea = getContentArea(
          aspectRatio,
          updatedSlide.slideTheme || 'default',
          newPosition
        );
        
        // 동적 레이아웃 대상 요소만 필터링 (제목, 도형 제외)
        const layoutElements = updatedSlide.elements.filter(el => {
          if (el.type === 'shape') {return false;}
          const isTitle = isTitleElement(el);
          if (isTitle && !includeTitleInLayout) {return false;}
          return true;
        });
        
        if (layoutElements.length > 0) {
          const positions = calculateObjectPositions(
            layoutOption,
            layoutElements.length,
            newContentArea,
            aspectRatio.width,
            aspectRatio.height,
            layoutSpacing === 'none' ? 0 : layoutSpacing === 'narrow' ? 0.01 : layoutSpacing === 'wide' ? 0.04 : 0.02
          );
          
          // 위치 업데이트 맵 생성
          const positionMap = new Map();
          layoutElements.forEach((el, index) => {
            if (index < positions.length) {
              positionMap.set(el.id, positions[index]);
            }
          });
          
          // 모든 요소 업데이트
          updatedSlide.elements = updatedSlide.elements.map(element => {
            const newPos = positionMap.get(element.id);
            return newPos ? { ...element, ...newPos } : element;
          });
        }
      } catch (error) {
        console.error('Dynamic layout parsing error:', error);
      }
    }
    
    // HTML 재생성은 updateSlideHtml에서 자동으로 처리됨
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
    
    // 규격 3번: 분할 위치 변경 후 기존 레이아웃 자동 재적용
    if (selectedLayoutOption && updatedSlide.elements && updatedSlide.elements.length > 0) {
      console.log('[분할 위치] 동적 레이아웃 즉시 적용 시작');
      
      // 콘텐츠 영역 재계산 (새로운 splitPosition 사용)
      const newContentArea = getContentArea(aspectRatio, updatedSlide.slideTheme || 'default', newPosition);
      
      // 레이아웃 적용할 요소들 필터링
      const contentElements = updatedSlide.elements.filter(element => {
        if (element.type === 'shape') {return false;}
        const isTitle = isTitleElement(element);
        if (isTitle && !includeTitleInLayout) {return false;}
        return true;
      });
      
      if (contentElements.length > 0) {
        // 레이아웃 위치 계산
        const positions = calculateObjectPositions(
          selectedLayoutOption,
          contentElements.length,
          newContentArea,
          aspectRatio.width,
          aspectRatio.height,
          layoutSpacing === 'none' ? 0 : layoutSpacing === 'narrow' ? 0.01 : layoutSpacing === 'wide' ? 0.04 : 0.02
        );
        
        // 요소 위치 업데이트
        const layoutUpdatedElements = updatedSlide.elements.map(element => {
          const contentIndex = contentElements.findIndex(el => el.id === element.id);
          if (contentIndex !== -1 && positions[contentIndex]) {
            return {
              ...element,
              x: positions[contentIndex].x,
              y: positions[contentIndex].y,
              width: positions[contentIndex].width,
              height: positions[contentIndex].height
            };
          }
          return element;
        });
        
        // 최종 슬라이드 업데이트
        const finalSlide = {
          ...updatedSlide,
          elements: layoutUpdatedElements
        };
        
        setTempSlide(finalSlide);
        console.log('[분할 위치] 동적 레이아웃 적용 완료');
      }
    }
  };

  // contentBgColor 업데이트 핸들러
  const handleContentBgColorChange = (newColor: string) => {
    if (!tempSlide) {return;}
    
    const updatedSlide = {
      ...tempSlide,
      contentBgColor: newColor
    };
    
    // HTML 재생성은 updateSlideHtml에서 자동으로 처리됨
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  // 구분선 타입 변경 핸들러
  const handleDividerTypeChange = (newType: DividerType) => {
    if (!tempSlide) {return;}
    
    const updatedSlide = {
      ...tempSlide,
      dividerType: newType,
    };
    
    // HTML 재생성은 updateSlideHtml에서 자동으로 처리됨
    setTempSlide(updatedSlide);
    setHasUnsavedChanges(true);
  };

  // 레이아웃 변경 핸들러
  const handleLayoutChange = (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
    // 그리드 레이아웃 변경 시 처리 로직
    console.log('Layout changed:', layout);
  };

  // 레이아웃 변경 timeout 참조
  const layoutChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 실제 레이아웃 변경 로직
  const handleDynamicLayoutChangeCore = useCallback((layoutOption?: LayoutOption) => {
    console.log('[동적 레이아웃] handleDynamicLayoutChangeCore 호출됨', { layoutOption, tempSlide: tempSlide?.id, elementsCount: tempSlide?.elements?.length });
    if (!tempSlide || !tempSlide.elements) {return;}
    
    // 동적 레이아웃 적용 전 제목 분리 및 배경 영역 배치
    const titleElements = tempSlide.elements.filter(element => isTitleElement(element));
    const nonTitleElements = tempSlide.elements.filter(element => !isTitleElement(element) && element.type !== 'shape');
    
    // 제목 위치가 업데이트된 요소 배열
    let elementsWithUpdatedTitles = tempSlide.elements;
    
    // includeTitleInLayout이 false인 경우 제목을 배경 영역에 배치
    if (!includeTitleInLayout && titleElements.length > 0) {
      const updatedTitleElements = titleElements.map(titleElement => {
        if (tempSlide.slideTheme && tempSlide.slideTheme !== 'default') {
          // 테마별 배경 영역 계산
          const backgroundArea = getBackgroundArea(
            aspectRatio,
            tempSlide.slideTheme,
            tempSlide.splitPosition || 25
          );
          const bgTitlePosition = getTitlePositionInBackground(backgroundArea, tempSlide.slideTheme, aspectRatio);
          
          return {
            ...titleElement,
            x: bgTitlePosition.x,
            y: bgTitlePosition.y,
            width: bgTitlePosition.width,
            height: bgTitlePosition.height,
            fontSize: bgTitlePosition.fontSize
          };
        } else {
          // 기본 테마일 경우 상단 중앙에 배치
          return {
            ...titleElement,
            x: 0.1,
            y: 0.05,
            width: 0.8,
            height: 0.1,
            fontSize: titleElement.fontSize && titleElement.fontSize < 32 ? 32 : titleElement.fontSize
          };
        }
      });
      
      // 제목 요소들을 업데이트된 위치로 교체 - 하지만 여기서는 setTempSlide를 호출하지 않음
      // 대신 아래에서 전체 레이아웃과 함께 한 번에 업데이트
      const titleUpdatedElements = tempSlide.elements.map(element => {
        const updatedTitle = updatedTitleElements.find(t => t.id === element.id);
        return updatedTitle || element;
      });
      
      // 제목이 업데이트된 요소 배열 사용
      elementsWithUpdatedTitles = titleUpdatedElements;
    } else {
      // 제목을 레이아웃에 포함하거나 제목이 없는 경우 원본 요소 배열 사용
      elementsWithUpdatedTitles = tempSlide.elements;
    }
    
    // 콘텐츠 객체들 필터링 (동적 레이아웃에 포함될 요소들)
    const contentElements = elementsWithUpdatedTitles.filter(element => {
      // 도형 제외 (항상)
      if (element.type === 'shape') {return false;}
      
      // includeTitleInLayout이 false면 제목 요소 제외
      if (!includeTitleInLayout && isTitleElement(element)) {return false;}
      
      // 나머지 모든 요소는 포함
      return true;
    });
    
    // 객체 수에 따른 가능한 레이아웃 옵션 가져오기
    const layoutOptions = getLayoutOptionsForObjectCount(contentElements.length);
    
    // 레이아웃 옵션 결정: 전달된 layoutOption이 없으면 랜덤 선택
    let selectedLayout = layoutOption;
    if (!layoutOption && layoutOptions.length > 0) {
      // 랜덤으로 레이아웃 선택
      const randomIndex = Math.floor(Math.random() * layoutOptions.length);
      selectedLayout = layoutOptions[randomIndex];
    } else if (!layoutOption) {
      // 레이아웃 옵션이 없으면 기본 레이아웃 사용
      selectedLayout = getDefaultLayoutOption(contentElements.length);
    }
    
    setSelectedLayoutOption(selectedLayout);
    
    // titleElements는 이미 위에서 선언되었음
    
    if (contentElements.length === 0) {return;}
    
    // 슬라이드 테마에 따른 콘텐츠 영역 계산
    let contentArea = getContentArea(
      aspectRatio, 
      tempSlide.slideTheme || 'default',
      tempSlide.splitPosition || 25
    );
    
    // 제목을 레이아웃에 포함하는 경우, 전체 영역 사용
    if (includeTitleInLayout) {
      // 제목 영역도 포함하여 전체 영역 사용
      contentArea = {
        ...contentArea,
        y: contentArea.y * 0.1, // 상단 여백만 약간 남김
        height: contentArea.height + (contentArea.y - contentArea.y * 0.1)
      };
    }
    
    // 간격 설정에 따른 마진 계산
    const spacingMap = {
      'none': 0,        // X - 간격 없음 (0)
      'narrow': 0.01,   // S - 좁은 간격
      'normal': 0.02,   // M - 보통 간격
      'wide': 0.04      // L - 넓은 간격
    };
    const spacing = spacingMap[layoutSpacing] || 0.02;
    
    // selectedLayout이 없으면 리턴
    if (!selectedLayout) {return;}
    
    // 새로운 레이아웃에 따른 위치 계산
    console.log('[동적 레이아웃] calculateObjectPositions 호출', { 
      selectedLayout, 
      contentElementsCount: contentElements.length, 
      contentArea,
      aspectRatio,
      spacing 
    });
    const newPositions = calculateObjectPositions(
      selectedLayout, 
      contentElements.length, 
      contentArea, 
      aspectRatio.width, 
      aspectRatio.height,
      spacing
    );
    console.log('[동적 레이아웃] 계산된 위치:', newPositions);
    
    // 제목 요소들을 배경 영역에 배치 (includeTitleInLayout이 false일 때)
    const titleElementsToReposition: { [id: string]: { x: number; y: number; width: number; height: number; fontSize?: number } } = {};
    if (!includeTitleInLayout && titleElements.length > 0) {
      if (tempSlide.slideTheme && tempSlide.slideTheme !== 'default') {
        // 슬라이드 테마에 따른 배경 영역 계산
        const backgroundArea = getBackgroundArea(
          aspectRatio,
          tempSlide.slideTheme,
          tempSlide.splitPosition || 25
        );
        
        // 배경 영역에서의 제목 위치 계산
        const titlePosition = getTitlePositionInBackground(
          backgroundArea,
          tempSlide.slideTheme,
          aspectRatio
        );
        
        titleElements.forEach(titleElement => {
          titleElementsToReposition[titleElement.id] = titlePosition;
        });
      } else {
        // 기본 테마일 경우 상단 중앙에 배치
        titleElements.forEach(titleElement => {
          titleElementsToReposition[titleElement.id] = {
            x: 0.5 - (titleElement.width || 0.8) / 2, // 중앙 정렬
            y: 0.05, // 상단 5% 위치 (여백 고려)
            width: titleElement.width || 0.8,
            height: titleElement.height || 0.1
          };
        });
      }
    }
    
    // 요소들의 위치 업데이트 - 제목이 이미 업데이트된 배열 사용
    const updatedElements = elementsWithUpdatedTitles.map(element => {
      // 도형은 건드리지 않음
      if (element.type === 'shape') {return element;}
      
      // 제목 판별
      const isTitle = (() => {
        if (element.type === 'title') {return true;}
        if (element.type === 'text') {
          const isRatioBased = element.x !== undefined && element.x <= 1 && 
                              element.y !== undefined && element.y <= 1;
          if (isRatioBased && element.y <= 0.15 && element.fontSize && element.fontSize >= 32) {
            return true;
          }
        }
        return false;
      })();
      
      // 제목이고 includeTitleInLayout이 false이면 상단 중앙으로 재배치
      if (isTitle && !includeTitleInLayout && titleElementsToReposition[element.id]) {
        return {
          ...element,
          ...titleElementsToReposition[element.id]
        };
      }
      
      const elementIndex = contentElements.findIndex(el => el.id === element.id);
      if (elementIndex >= 0 && elementIndex < newPositions.length) {
        const position = newPositions[elementIndex];
        return {
          ...element,
          x: position.x,
          y: position.y,
          width: position.width,
          height: position.height
        };
      }
      
      return element;
    });
    
    const updatedSlide = {
      ...tempSlide,
      elements: updatedElements
    };
    
    console.log('[동적 레이아웃] 업데이트된 요소들:', updatedElements.map(el => ({ 
      id: el.id, 
      type: el.type, 
      x: el.x, 
      y: el.y, 
      width: el.width, 
      height: el.height 
    })));
    
    // HTML 재생성
    const updatedHtmlContent = regenerateHtmlContent(updatedSlide);
    
    setTempSlide({
      ...updatedSlide,
      htmlContent: updatedHtmlContent
    });
    setHasUnsavedChanges(true);
  }, [tempSlide, aspectRatio, includeTitleInLayout, layoutSpacing]);

  // 레이아웃 UI 업데이트 함수
  const updateLayoutUI = useCallback((layoutOption: LayoutOption) => {
    setSelectedLayoutOption(layoutOption);
  }, []);

  // 레이아웃 적용 함수 (UI 업데이트 없이)
  const applyLayout = useCallback((layoutOption?: LayoutOption) => {
    console.log('[동적 레이아웃] applyLayout 호출됨', layoutOption);
    if (layoutChangeTimeoutRef.current) {
      clearTimeout(layoutChangeTimeoutRef.current);
    }
    
    layoutChangeTimeoutRef.current = setTimeout(() => {
      handleDynamicLayoutChangeCore(layoutOption);
    }, 100);
  }, [handleDynamicLayoutChangeCore]);

  // 동적 레이아웃 변경 핸들러 (사용자 액션용)
  const handleDynamicLayoutChange = useCallback((layoutOption: LayoutOption) => {
    console.log('[동적 레이아웃] handleDynamicLayoutChange 호출됨', layoutOption);
    updateLayoutUI(layoutOption);  // UI 업데이트
    applyLayout(layoutOption);     // 레이아웃 적용
  }, [updateLayoutUI, applyLayout]);

  const exportSlideAsImage = async (slide: Slide) => {
    // 최신 HTML 확인 - tempSlide가 현재 슬라이드인 경우 최신 HTML 사용
    let finalHtmlContent = slide.htmlContent;
    if (tempSlide && tempSlide.id === slide.id) {
      // 현재 편집 중인 슬라이드인 경우, 최신 HTML 재생성
      finalHtmlContent = regenerateHtmlContent(tempSlide);
    }
    
    // 새 창에서 이미지 생성 처리
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      alert(t.popupBlocked || (language === 'ko' ? '팝업이 차단되었습니다. 팝업을 허용해주세요.' : 'Popup blocked. Please allow popups.'));
      return;
    }

    newWindow.document.write(`
      <html>
        <head>
          <title>${t.generatingImage || (language === 'ko' ? '이미지 생성 중...' : 'Generating image...')}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              margin: 0;
              background: #f5f5f5;
            }
            .container {
              text-align: center;
              padding: 2rem;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            .spinner {
              border: 4px solid #f3f3f3;
              border-top: 4px solid #3498db;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto 1rem;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="spinner"></div>
            <h2>${t.generatingSlideImage || (language === 'ko' ? `슬라이드 ${slide.order + 1} 이미지 생성 중...` : `Generating slide ${slide.order + 1} image...`)}</h2>
            <p>${t.pleaseWait || (language === 'ko' ? '잠시만 기다려주세요.' : 'Please wait.')}</p>
          </div>
        </body>
      </html>
    `);

    try {
      const { default: html2canvas } = await import('html2canvas');
      const slideContainer = document.querySelector(`[data-slide-id="${slide.id}"]`) as HTMLElement;

      let canvas;
      if (slideContainer) {
        // 슬라이드 컨테이너 내부의 실제 SlideRenderer를 찾기
        const slideRenderer = slideContainer.querySelector('.slide-renderer-container') as HTMLElement;
        const targetElement = slideRenderer || slideContainer;
        
        canvas = await html2canvas(targetElement, {
          width: targetElement.offsetWidth,
          height: targetElement.offsetHeight,
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true,
          logging: false,
          onclone: (clonedDoc) => {
            // 클론된 문서에서 스타일 보정
            const clonedElement = clonedDoc.querySelector(`[data-slide-id="${slide.id}"] .slide-renderer-container`) || 
                                clonedDoc.querySelector(`[data-slide-id="${slide.id}"]`);
            if (clonedElement) {
              (clonedElement as HTMLElement).style.transform = 'none';
              (clonedElement as HTMLElement).style.transformOrigin = 'initial';
            }
          }
        });
      } else {
        // Fallback - 현재 슬라이드 상태로 임시 렌더링
        const tempDiv = document.createElement('div');
        try {
          tempDiv.innerHTML = finalHtmlContent;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = `${aspectRatio.width}px`;
          tempDiv.style.height = `${aspectRatio.height}px`;
          tempDiv.style.border = `${slideBorderStyle?.borderWidth || 1}px ${slideBorderStyle?.borderStyle || 'solid'} #E5E7EB`;
          tempDiv.style.borderRadius = `${slideBorderStyle?.borderRadius || 8}px`;
          tempDiv.style.boxShadow = slideBorderStyle?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)';
          document.body.appendChild(tempDiv);

          canvas = await html2canvas(tempDiv, {
            width: aspectRatio.width,
            height: aspectRatio.height,
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
          });
        } finally {
          // DOM 정리 보장
          if (tempDiv.parentNode) {
            document.body.removeChild(tempDiv);
          }
          // innerHTML 클리어하여 메모리 해제
          tempDiv.innerHTML = '';
        }
      }

      // 완료 후 다운로드
      newWindow.document.body.innerHTML = `
        <div class="container">
          <h2>✅ ${t.imageGenerationComplete || (language === 'ko' ? '이미지 생성 완료!' : 'Image generation complete!')}</h2>
          <p>${t.downloadStarting || (language === 'ko' ? '다운로드가 시작됩니다...' : 'Download starting...')}</p>
          <button onclick="window.close()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer;">${t.closeWindow || (language === 'ko' ? '창 닫기' : 'Close Window')}</button>
        </div>
      `;

      const link = document.createElement('a');
      link.download = `slide-${slide.order + 1}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      // 2초 후 창 자동 닫기
      setTimeout(() => {
        newWindow.close();
      }, 2000);

    } catch {
      // Image export error handled without logging sensitive information
      newWindow.document.body.innerHTML = `
        <div class="container">
          <h2>❌ ${t.errorOccurred || (language === 'ko' ? '오류 발생' : 'Error occurred')}</h2>
          <p>${t.imageGenerationError || (language === 'ko' ? '이미지 생성 중 오류가 발생했습니다.' : 'An error occurred while generating the image.')}</p>
          <button onclick="window.close()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #e74c3c; color: white; border: none; border-radius: 4px; cursor: pointer;">${t.closeWindow || (language === 'ko' ? '창 닫기' : 'Close Window')}</button>
        </div>
      `;
    }
  };


  // 선택된 요소 정렬 함수
  const applyElementAlignment = (alignment: 'left' | 'center' | 'right') => {
    if (!tempSlide || !selectedElementId) {return;}

    // 현재 편집 모드의 실제 슬라이드 크기 가져오기
    // editModeScaling에서 계산된 실제 크기 사용
    const containerWidth = editModeScaling.actualWidth;
    const containerHeight = editModeScaling.actualHeight;
    
    // Handle built-in elements (title, content)
    if (selectedElementId === 'slide-title' || selectedElementId === 'slide-content') {
      const elementKey = `${selectedElementId.replace('slide-', '')}Position`;
      const defaultPosition = {
        x: selectedElementId === 'slide-title' ? containerWidth * 0.1 : containerWidth * 0.1,
        y: selectedElementId === 'slide-title' ? containerHeight * 0.2 : containerHeight * 0.4,
        width: selectedElementId === 'slide-title' ? containerWidth * 0.8 : containerWidth * 0.8,
        height: selectedElementId === 'slide-title' ? containerHeight * 0.15 : containerHeight * 0.3,
      };
      
      const currentPosition = tempSlide[elementKey] || defaultPosition;
      
      // 비율 기반인 경우 픽셀로 변환
      const actualWidth = currentPosition.width < 1 ? currentPosition.width * containerWidth : currentPosition.width;
      const actualX = currentPosition.x < 1 ? currentPosition.x * containerWidth : currentPosition.x;

      let newX = actualX;

      switch (alignment) {
        case 'left':
          newX = 20; // 좌측 여백
          break;
        case 'center':
          newX = (containerWidth - actualWidth) / 2; // 가운데 정렬
          break;
        case 'right':
          newX = containerWidth - actualWidth - 20; // 우측 여백
          break;
      }

      setTempSlide(prev => prev ? {
        ...prev,
        [elementKey]: {
          ...currentPosition,
          x: newX,
        }
      } : null);
      setHasUnsavedChanges(true);
      return;
    }

    // Handle regular elements
    const element = tempSlide.elements?.find(el => el.id === selectedElementId);
    if (!element) {return;}

    // 비율 기반 요소인지 확인 (구조화된 요소들)
    const isRatioBased = element.x < 1 && element.y < 1 && element.width < 1 && element.height < 1;
    
    // 실제 픽셀 크기 계산
    const actualWidth = isRatioBased ? element.width * containerWidth : element.width;
    const margin = 20; // 좌우 여백
    
    let newX = element.x;

    if (isRatioBased) {
      // 비율 기반 요소도 픽셀로 변환하여 정렬
      switch (alignment) {
        case 'left':
          newX = margin / containerWidth; // 픽셀을 비율로 변환
          break;
        case 'center':
          newX = (containerWidth - actualWidth) / 2 / containerWidth; // 가운데 정렬을 비율로
          break;
        case 'right':
          newX = (containerWidth - actualWidth - margin) / containerWidth; // 우측 정렬을 비율로
          break;
      }
    } else {
      // 픽셀 기반 정렬
      switch (alignment) {
        case 'left':
          newX = margin; // 좌측 여백
          break;
        case 'center':
          newX = (containerWidth - actualWidth) / 2; // 가운데 정렬
          break;
        case 'right':
          newX = containerWidth - actualWidth - margin; // 우측 여백
          break;
      }
    }

    updateElement(selectedElementId, { x: newX });
  };

  const selectedElement = tempSlide?.elements?.find(el => el.id === selectedElementId);

  // Calculate slide container size based on aspect ratio - responsive
  // Issue #2 fix: Make getSlideContainerStyle truly responsive
  const getSlideContainerStyle = () => {
    // 모든 슬라이드가 동일한 크기를 사용
    return editModeScaling.containerStyle;
  };

  // Render editing controls in top toolbar via portal
  const renderEditingControlsInToolbar = () => {
    const toolbarContainer = document.getElementById('slide-editing-controls');
    if (!toolbarContainer || !activeSlideId || !tempSlide) {return null;}

    // Get layout options for the current elements
    const objectCount = countContentObjects(tempSlide.elements || [], aspectRatio.height, includeTitleInLayout);
    const layoutOptions = getLayoutOptionsForObjectCount(objectCount);

    return createPortal(
      <div className="flex flex-col gap-2 p-2">
        {/* First Row: Layout, Theme, Aspect Ratio */}
        <div className="flex items-stretch gap-2">
          {/* Layout Control */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
            <span className={`text-xs font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ko' ? `레이아웃` : `Layout`}
            </span>
            
            {/* Layout Options */}
            <div className="flex items-center gap-1">
              {layoutOptions.slice(0, 6).map((option, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDynamicLayoutChange(option);
                  }}
                  className={`p-1 rounded border transition-colors ${
                    selectedLayoutOption?.type === option.type
                      ? isDarkMode
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-blue-500 border-blue-400 text-white'
                      : isDarkMode
                        ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-300'
                        : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                  title={option.description}
                >
                  <div className="w-5 h-4 flex">
                    {option.columns.map((itemCount, colIndex) => (
                      <div key={colIndex} className="flex-1 flex flex-col justify-evenly px-0.5">
                        {Array.from({ length: Math.min(itemCount, 3) }).map((_, itemIndex) => (
                          <div
                            key={itemIndex}
                            className={`w-full rounded-sm ${
                              selectedLayoutOption?.type === option.type
                                ? 'bg-blue-200'
                                : isDarkMode ? 'bg-gray-400' : 'bg-gray-400'
                            }`}
                            style={{ height: '1px' }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Spacing Controls */}
            <div className="border-l pl-2 flex items-center gap-1">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'ko' ? '간격' : 'Gap'}
              </span>
              {[
                { key: 'none', label: 'X' },
                { key: 'narrow', label: 'S' },
                { key: 'normal', label: 'M' },
                { key: 'wide', label: 'L' }
              ].map((spacing) => (
                <button
                  key={spacing.key}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLayoutSpacing(spacing.key as 'narrow' | 'normal' | 'wide');
                    // 간격 변경 시에는 선택된 레이아웃이 있을 때만 레이아웃 재계산
                    if (selectedLayoutOption) {
                      applyLayout(selectedLayoutOption);  // UI 업데이트 없이 레이아웃만 재적용
                    }
                  }}
                  className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                    layoutSpacing === spacing.key
                      ? isDarkMode
                        ? 'bg-purple-600 border-purple-500 text-white'
                        : 'bg-purple-500 border-purple-400 text-white'
                      : isDarkMode
                        ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-300'
                        : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                  title={spacing.key}
                >
                  {spacing.label}
                </button>
              ))}
            </div>
          </div>

          {/* Theme Control */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
            <span className={`text-xs font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ko' ? '테마' : 'Theme'}
            </span>
            {/* Theme Buttons */}
            <div className="flex items-center gap-1">
              {[
                { id: 'default', icon: '□', name: '기본' },
                { id: 'left-split', icon: '▌', name: '좌측' },
                { id: 'right-split', icon: '▐', name: '우측' },
                { id: 'top-split', icon: '▀', name: '상단' },
                { id: 'bottom-split', icon: '▄', name: '하단' },
                { id: 'diagonal', icon: '◢', name: '사선' }
              ].map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleSlideThemeChange(theme.id as 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'diagonal')}
                  className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                    (tempSlide?.slideTheme || 'default') === theme.id
                      ? isDarkMode
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-blue-500 border-blue-400 text-white'
                      : isDarkMode
                        ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-300'
                        : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                  title={theme.name}
                >
                  {theme.icon}
                </button>
              ))}
            </div>
            {/* Divider Type */}
            <div className="border-l pl-2">
              <select
                value={tempSlide?.dividerType || 'straight'}
                onChange={(e) => handleDividerTypeChange(e.target.value as 'straight' | 'convex' | 'concave' | 'zigzag' | 'cloud' | 'jagged' | 'gradient' | 'random')}
                className={`text-xs px-1 py-0.5 rounded border ${
                  isDarkMode
                    ? 'bg-gray-600 border-gray-500 text-white'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                <option value="straight">{language === 'ko' ? '직선' : 'Straight'}</option>
                <option value="convex">{language === 'ko' ? '볼록' : 'Convex'}</option>
                <option value="concave">{language === 'ko' ? '오목' : 'Concave'}</option>
                <option value="zigzag">{language === 'ko' ? '지그재그' : 'Zigzag'}</option>
                <option value="cloud">{language === 'ko' ? '구름' : 'Cloud'}</option>
                <option value="jagged">{language === 'ko' ? '톱니' : 'Jagged'}</option>
              </select>
            </div>
            
          </div>

        </div>

        {/* Second Row: Background Controls */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
          <span className={`text-xs font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {language === 'ko' ? '배경' : 'Background'}
          </span>
          
          {/* Background Type Selector */}
          <div className="flex gap-1">
            {[
              { type: 'image', emoji: '🖼️', name: language === 'ko' ? '이미지' : 'Image' },
              { type: 'color', emoji: '🎨', name: language === 'ko' ? '단색' : 'Color' },
              { type: 'animated', emoji: '✨', name: language === 'ko' ? '애니메이션' : 'Animation' }
            ].map((bgType) => (
              <button
                key={bgType.type}
                onClick={() => {
                  if (!tempSlide) {return;}
                  const updatedSlide = { ...tempSlide, backgroundType: bgType.type as 'image' | 'color' | 'animated' };
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                }}
                className={`px-2 py-1 text-xs rounded border transition-colors ${
                  (tempSlide.backgroundType || 'image') === bgType.type
                    ? isDarkMode
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-blue-500 border-blue-400 text-white'
                    : isDarkMode
                      ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-300'
                      : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
                title={bgType.name}
              >
                {bgType.emoji}
              </button>
            ))}
          </div>

          {/* Background specific controls based on type */}
          {(tempSlide.backgroundType || 'image') === 'image' && (
            <>
              <div className="border-l pl-2 flex items-center gap-2">
                <button
                  onClick={() => setShowPixabaySearch(true)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    isDarkMode
                      ? 'bg-green-600 border-green-500 hover:bg-green-500 text-white'
                      : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                  }`}
                  title={language === 'ko' ? '이미지 검색' : 'Image Search'}
                >
                  🔍 검색
                </button>
                <button
                  onClick={handleBackgroundImageUpload}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    isDarkMode
                      ? 'bg-blue-600 border-blue-500 hover:bg-blue-500 text-white'
                      : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                  }`}
                  title={language === 'ko' ? '이미지 업로드' : 'Upload Image'}
                >
                  📁 업로드
                </button>
              </div>
              
              <div className="border-l pl-2 flex items-center gap-2">
                <label className="text-xs">{language === 'ko' ? '블러' : 'Blur'}</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={tempSlide.backgroundBlur || 2}
                  onChange={(e) => handleBackgroundBlurChange(parseInt(e.target.value))}
                  className="w-16"
                />
                <span className="text-xs w-4">{tempSlide.backgroundBlur || 2}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={tempSlide.backgroundGrayscale || false}
                  onChange={(e) => handleBackgroundGrayscaleChange(e.target.checked)}
                  className="w-3 h-3"
                />
                <label className="text-xs">{language === 'ko' ? '흑백' : 'B&W'}</label>
              </div>
            </>
          )}

          {(tempSlide.backgroundType || 'image') === 'color' && (
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={tempSlide.backgroundColor || '#3B82F6'}
                onChange={(e) => {
                  if (!tempSlide) {return;}
                  const updatedSlide = { ...tempSlide, backgroundColor: e.target.value };
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                }}
                className="w-8 h-6 rounded border cursor-pointer"
              />
              
              <div className="flex gap-1">
                {['#FFFFFF', '#000000', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD', '#FFD93D'].map((color) => (
                  <button
                    key={color}
                    onClick={() => {
                      if (!tempSlide) {return;}
                      const updatedSlide = { ...tempSlide, backgroundColor: color };
                      setTempSlide(updatedSlide);
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-4 h-4 rounded border ${
                      tempSlide.backgroundColor === color ? 'border-2 border-blue-500' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}

          {(tempSlide.backgroundType || 'image') === 'animated' && (
            <div className="flex items-center gap-2">
              <select
                value={tempSlide.animatedBackgroundEffect || 'birds'}
                onChange={(e) => {
                  if (!tempSlide) {return;}
                  const updatedSlide = { ...tempSlide, animatedBackgroundEffect: e.target.value };
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                }}
                className={`text-xs px-2 py-1 rounded border ${
                  isDarkMode
                    ? 'bg-gray-600 border-gray-500 text-white'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                <option value="birds">{language === 'ko' ? '새' : 'Birds'}</option>
                <option value="fog">{language === 'ko' ? '안개' : 'Fog'}</option>
                <option value="waves">{language === 'ko' ? '물결' : 'Waves'}</option>
                <option value="clouds">{language === 'ko' ? '구름' : 'Clouds'}</option>
                <option value="globe">{language === 'ko' ? '지구' : 'Globe'}</option>
                <option value="net">{language === 'ko' ? '네트' : 'Net'}</option>
                <option value="cells">{language === 'ko' ? '세포' : 'Cells'}</option>
                <option value="trunk">{language === 'ko' ? '나무' : 'Trunk'}</option>
                <option value="topology">{language === 'ko' ? '토폴로지' : 'Topology'}</option>
                <option value="dots">{language === 'ko' ? '점' : 'Dots'}</option>
                <option value="rings">{language === 'ko' ? '고리' : 'Rings'}</option>
                <option value="halo">{language === 'ko' ? '헤일로' : 'Halo'}</option>
              </select>
            </div>
          )}

          {/* Pattern - Always visible */}
          <div className="border-l pl-2">
            <select
              value={tempSlide.backgroundPattern || 'none'}
              onChange={(e) => {
                if (!tempSlide) {return;}
                const updatedSlide = { ...tempSlide, backgroundPattern: e.target.value };
                setTempSlide(updatedSlide);
                setHasUnsavedChanges(true);
              }}
              className={`text-xs px-1 py-0.5 rounded border ${
                isDarkMode
                  ? 'bg-gray-600 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="none">{language === 'ko' ? '패턴 없음' : 'No Pattern'}</option>
              <option value="grid-small">{language === 'ko' ? '좁은 격자' : 'Small Grid'}</option>
              <option value="grid-large">{language === 'ko' ? '넓은 격자' : 'Large Grid'}</option>
              <option value="lines-horizontal">{language === 'ko' ? '가로줄' : 'Horizontal'}</option>
              <option value="lines-vertical">{language === 'ko' ? '세로줄' : 'Vertical'}</option>
              <option value="dots">{language === 'ko' ? '점' : 'Dots'}</option>
            </select>
          </div>
        </div>
      </div>,
      toolbarContainer
    );
  };

  return (
    <>
      {/* Hidden file input for image/video uploads */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept="image/*,video/*"
        onChange={(e) => {
          console.log('File input onChange triggered', e.target.files);
        }}
      />
      
      <div className="space-y-1 relative" ref={slideEditorRef}>

      <div className="space-y-1">
        {slides.map((slide, index) => (
          <React.Fragment key={slide.id}>
            <div className="relative">

              {/* 슬라이드 선택 시 주변에 편집 도구 표시 */}
              {activeSlideId === slide.id ? (
                <div className="relative z-20">
                  {/* 스크롤 타겟 - 편집창 상단 */}
                  <div ref={activeSlideId === slide.id ? scrollToRef : null} />
                  {/* 통합된 편집 컨트롤 패널 */}
                  {activeSlideId && tempSlide && (
                    <SlideEditingPanel
                      tempSlide={tempSlide}
                      theme={theme}
                      aspectRatio={aspectRatio}
                      selectedLayoutOption={selectedLayoutOption}
                      layoutSpacing={layoutSpacing}
                      includeTitleInLayout={includeTitleInLayout}
                      isDarkMode={isDarkMode}
                      language={language}
                      showPixabaySearch={showPixabaySearch}
                      activeSection={editingPanelActiveSection}
                      onActiveSectionChange={setEditingPanelActiveSection}
                      onThemeChange={(newTheme) => onThemeChange?.(newTheme)}
                      onSlideThemeChange={handleSlideThemeChange}
                      onSplitPositionChange={handleSplitPositionChange}
                      onContentBgColorChange={handleContentBgColorChange}
                      onDividerTypeChange={handleDividerTypeChange}
                      onDynamicLayoutChange={handleDynamicLayoutChange}
                      onLayoutSpacingChange={(spacing) => {
                        setLayoutSpacing(spacing);
                        // 간격 변경 시 현재 선택된 레이아웃을 다시 적용
                        if (selectedLayoutOption) {
                          applyLayout(selectedLayoutOption);  // UI 업데이트 없이 레이아웃만 재적용
                        }
                      }}
                      onIncludeTitleInLayoutChange={setIncludeTitleInLayout}
                      onBackgroundSeedChange={handleSeedChange}
                      onBackgroundBlurChange={(blur) => {
                        if (!tempSlide) {return;}
                        const updatedSlide = { ...tempSlide, backgroundBlur: blur };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                      onBackgroundGrayscaleChange={(grayscale) => {
                        if (!tempSlide) {return;}
                        const updatedSlide = { ...tempSlide, backgroundGrayscale: grayscale };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                      onBackgroundChange={handleBackgroundImageChange}
                      onBackgroundPatternChange={(pattern) => {
                        if (!tempSlide) {return;}
                        const updatedSlide = { ...tempSlide, backgroundPattern: pattern };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                      onBackgroundTypeChange={handleBackgroundTypeChange}
                      onAnimatedEffectChange={handleAnimatedEffectChange}
                      onAnimatedOptionsChange={handleAnimatedOptionsChange}
                      onImageElementAdd={(imageUrl: string) => {
                        // 이미지를 슬라이드 요소로 추가하는 간단한 구현
                        if (!tempSlide) {return;}
                        const newElement: SlideElement = {
                          id: `img-${Date.now()}`,
                          type: 'image',
                          content: imageUrl,
                          x: 0.1,
                          y: 0.1,
                          width: 0.3,
                          height: 0.3,
                          fontSize: 16,
                          color: '#000000'
                        };
                        const updatedSlide = {
                          ...tempSlide,
                          elements: [...(tempSlide.elements || []), newElement]
                        };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                      setShowPixabaySearch={setShowPixabaySearch}
                      slides={slides}
                      activeSlideId={activeSlideId}
                      activeTab={activeTab}
                      showPromptPanel={showPromptPanel}
                      displayScale={displayScale}
                      onTabChange={onTabChange}
                      onSlideMove={onSlideMove}
                      onSlideSelect={onSlideSelect}
                      onPromptPanelToggle={() => setShowPromptPanel(!showPromptPanel)}
                      onDisplayScaleChange={handleDisplayScaleChange}
                      onSaveAndExit={() => {
                        // 변경사항이 있으면 먼저 저장
                        if (tempSlide && hasUnsavedChanges) {
                          saveSlide();
                        }
                        // 저장 완료 후 종료
                        setTimeout(() => {
                          onSlideSelect('');
                        }, 100); // 저장 완료 대기
                      }}
                      onTextElementAdd={() => setShowTextTypeSelector(true)}
                      onShapeElementAdd={() => setShowShapeSelector(true)}
                      onChartElementAdd={() => addNewElement('chart')}
                      onImageElementUpload={handleImageUpload}
                      onVideoElementUpload={handleVideoUpload}
                      onLayerPanelOpen={() => setShowLayerPanel(!showLayerPanel)}
                      onRightPanelToggle={onRightPanelToggle} // 새로 추가
                      themeFont={themeFont}
                      onThemeFontChange={onThemeFontChange}
                      slideBorderStyle={slideBorderStyle}
                      onSlideBorderStyleChange={onSlideBorderStyleChange}
                      onFontBorderChange={(enabled) => {
                        if (!tempSlide) {return;}
                        const updatedSlide = { ...tempSlide, fontBorder: enabled };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                      onFontGlowChange={(enabled) => {
                        if (!tempSlide) {return;}
                        const updatedSlide = { ...tempSlide, fontGlow: enabled };
                        setTempSlide(updatedSlide);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  )}

                  {/* 메인 편집 영역 - 좌우 배치 + 프롬프트 패널 */}
                  <div className="relative" style={{ marginTop: '0px', marginLeft: '0px' }}>
                    <div className="relative justify-start items-start" style={{ minHeight: 'auto' }}>
                    {/* 좌측: 슬라이드 */}
                    <div style={{ display: 'inline-block' }}>
                      {activeTab === 'preview' ? (
                        <div
                          ref={containerRef}
                          data-slide-id={slide.id}
                          style={{ 
                            position: 'relative',
                            width: `${editModeScaling.actualWidth}px`,
                            height: `${editModeScaling.actualHeight}px`,
                            padding: 0,
                            margin: 0,
                            cursor: 'crosshair',
                            overflow: 'visible',
                            display: 'inline-block',
                            boxSizing: 'border-box'
                          }}
                          onClick={(e) => {
                            // 빈 공간 클릭 시 모든 선택 해제 (단일 및 다중 선택)
                            if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('slide-renderer-container')) {
                              setSelectedElementId(undefined);
                              setSelectedElementIds([]); // 다중 선택도 해제
                              setEditingElement(null);
                            }
                          }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                        >
                          {tempSlide ? (
                            <SlideRenderer
                              slide={tempSlide}
                              theme={theme}
                              aspectRatio={aspectRatio}
                              language={language}
                              themeFont={themeFont}
                              borderStyle={slideBorderStyle}
                              isEditable={true}
                              scale={editModeScaling.scale}
                              containerWidth={editModeScaling.actualWidth}
                              containerHeight={editModeScaling.actualHeight}
                              disableTransformScale={true}
                              selectedElementId={selectedElementId}
                              selectedElementIds={selectedElementIds}
                              editingElementId={editingElement}
                              onElementSelect={(elementId: string, event?: MouseEvent | React.MouseEvent) => {
                                const shiftKey = event?.shiftKey || false;
                                handleElementSelect(elementId, shiftKey);
                              }}
                              onElementUpdate={updateElement}
                              onMultiElementUpdate={(updates) => {
                                setTempSlide(prev => prev ? {
                                ...prev,
                                elements: (prev.elements || []).map(el => {
                                  const update = updates.find(u => u.id === el.id);
                                  return update ? { ...el, ...update.updates } : el;
                                })
                              } : null);
                              setHasUnsavedChanges(true);
                            }}
                            onElementDelete={deleteElement}
                            onEditingElementChange={setEditingElement}
                            onLayoutChange={handleLayoutChange}
                            onSlideUpdate={(updates) => {
                              setTempSlide(prev => prev ? { ...prev, ...updates } : null);
                              setHasUnsavedChanges(true);
                            }}
                            isDarkMode={isDarkMode}
                          />
                          ) : (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                              <div className="text-center">
                                <div className="text-lg mb-2">⏳</div>
                                <div className="text-sm">{language === 'ko' ? '슬라이드 로딩 중...' : 'Loading slide...'}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* HTML 편집 모드 */
                        <div className="border rounded-lg" style={{ 
                          display: 'inline-block', 
                          position: 'relative',
                          width: `${editModeScaling.actualWidth}px`,
                          height: `${editModeScaling.actualHeight}px`
                        }}>
                          <textarea
                            value={(() => {
                              // HTML 보기에서는 base64 이미지를 경로로 표시
                              let htmlContent = tempSlide?.htmlContent || '';
                              
                              // base64 이미지를 파일 경로로 변환
                              htmlContent = htmlContent.replace(
                                /src="data:image\/[^;]+;base64,[^"]+"/g,
                                (match) => {
                                  // base64 형식을 감지하여 간단한 플레이스홀더로 변경
                                  if (match.includes('image/jpeg')) {
                                    return 'src="[image.jpg]"';
                                  } else if (match.includes('image/png')) {
                                    return 'src="[image.png]"';
                                  } else if (match.includes('image/gif')) {
                                    return 'src="[image.gif]"';
                                  } else if (match.includes('image/svg')) {
                                    return 'src="[image.svg]"';
                                  }
                                  return 'src="[image]"';
                                }
                              );
                              
                              // background-image의 base64도 변환
                              htmlContent = htmlContent.replace(
                                /background-image:\s*url\(["']?data:image\/[^;]+;base64,[^"')]+["']?\)/g,
                                'background-image: url([background-image])'
                              );
                              
                              return htmlContent;
                            })()}
                            onChange={(e) => {
                              setTempSlide(prev => prev ? { ...prev, htmlContent: e.target.value } : null);
                              setHasUnsavedChanges(true);
                            }}
                            className={`w-full h-full p-4 font-mono text-sm resize-none ${
                              isDarkMode ? 'bg-gray-900 text-gray-100 border-gray-600' : 'bg-white text-gray-900 border-gray-300'
                            }`}
                            placeholder="HTML 코드를 직접 편집하세요..."
                          />
                        </div>
                      )}
                    </div>

                    {/* 우측: 편집 도구 */}
                    <div className="w-80 space-y-4" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                      {/* Layout/Theme/Background controls moved to top toolbar */}

                      {/* 선택된 요소의 속성 패널은 우측 팝업으로 이동됨 */}
                    </div>
                  </div>
                    
                    {/* 프롬프트 패널 - Desktop/Mobile 반응형 */}
                    {showPromptPanel && tempSlide && (
                      <>
                        {/* Desktop: 기존 방식 */}
                        <div className="hidden md:block">
                          <SlidePromptPanel
                        slide={tempSlide}
                        onRegenerate={async (prompt, objectType) => {
                          if (!geminiApiKey) {
                            setShowToast({
                              message: language === 'ko' ? 'API 키가 설정되지 않았습니다.' : 'API key is not configured.',
                              type: 'error'
                            });
                            return;
                          }

                          try {
                            if (objectType) {
                              // 새로운 객체 유형 지정 재생성
                              const newStructuredContent = await regenerateSlideContentWithType(
                                tempSlide.title,
                                tempSlide.content,
                                prompt,
                                geminiApiKey,
                                language,
                                objectType
                              );
                              
                              const newElements = await createStructuredElements(
                                newStructuredContent, 
                                theme, 
                                aspectRatio, 
                                themeFont, 
                                tempSlide.slideLayout || 'title-top-content-bottom',
                                tempSlide.keywords
                              );
                              
                              const updatedSlide = {
                                ...tempSlide,
                                elements: [
                                  ...(tempSlide.elements || []).filter(el => !el.id.includes('content-')), // 기존 요소 중 title과 수동 요소만 유지
                                  ...newElements.filter(el => !el.id.includes('title-')) // 새로운 content 요소만 추가
                                ]
                              };
                              
                              setTempSlide(updatedSlide);
                              
                            } else {
                              // 기존 방식 재생성
                              const result = await regenerateSlideContent(tempSlide, prompt, geminiApiKey, language);
                              
                              const updatedSlide = {
                                ...tempSlide,
                                title: result.title,
                                content: result.content,
                                // JSON 구조가 있으면 요소로 변환하여 기존 요소와 합치기
                                ...(result.structuredContent && {
                                  elements: [
                                    ...(tempSlide.elements || []).filter(el => !el.id.includes('title-') && !el.id.includes('content-')), // 기존 수동 요소 유지
                                    ...(await createStructuredElements(result.structuredContent, theme, aspectRatio, themeFont, tempSlide.slideLayout || 'title-top-content-bottom', tempSlide.keywords))
                                  ]
                                })
                              };
                              
                              setTempSlide(updatedSlide);
                            }
                            setHasUnsavedChanges(true);
                            
                            setShowToast({
                              message: language === 'ko' ? '슬라이드가 재생성되었습니다.' : 'Slide has been regenerated.',
                              type: 'success'
                            });
                          } catch (error) {
                            console.error('Failed to regenerate slide:', error);
                            setShowToast({
                              message: language === 'ko' ? '슬라이드 재생성에 실패했습니다.' : 'Failed to regenerate slide.',
                              type: 'error'
                            });
                          }
                        }}
                        onClose={() => setShowPromptPanel(false)}
                        language={language}
                        isDarkMode={isDarkMode}
                      />
                        </div>
                        
                        {/* Mobile: Modal 방식 */}
                        <div className="md:hidden fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                          <div className={`w-full max-w-lg max-h-[80vh] rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 overflow-y-auto m-4`}>
                            {/* Modal Header */}
                            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {language === 'ko' ? '프롬프트' : 'Prompt'}
                              </h3>
                              <button
                                onClick={() => setShowPromptPanel(false)}
                                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            
                            {/* Modal Content */}
                            <SlidePromptPanel
                              slide={tempSlide}
                              onRegenerate={async (prompt, objectType) => {
                                if (!geminiApiKey) {
                                  setShowToast({
                                    message: language === 'ko' ? 'API 키가 설정되지 않았습니다.' : 'API key is not configured.',
                                    type: 'error'
                                  });
                                  return;
                                }

                                try {
                                  if (objectType) {
                                    // 새로운 객체 유형 지정 재생성
                                    const newStructuredContent = await regenerateSlideContentWithType(
                                      tempSlide.title,
                                      tempSlide.content,
                                      prompt,
                                      geminiApiKey,
                                      language,
                                      objectType
                                    );
                                    
                                    const newElements = await createStructuredElements(
                                      newStructuredContent, 
                                      theme, 
                                      aspectRatio, 
                                      themeFont, 
                                      tempSlide.slideLayout || 'title-top-content-bottom',
                                      tempSlide.keywords
                                    );
                                    
                                    const updatedSlide = {
                                      ...tempSlide,
                                      elements: [
                                        ...(tempSlide.elements || []).filter(el => !el.id.includes('content-')), // 기존 요소 중 title과 수동 요소만 유지
                                        ...newElements.filter(el => !el.id.includes('title-')) // 새로운 content 요소만 추가
                                      ]
                                    };
                                    
                                    setTempSlide(updatedSlide);
                                    
                                  } else {
                                    // 기존 방식 재생성
                                    const result = await regenerateSlideContent(tempSlide, prompt, geminiApiKey, language);
                                    
                                    const updatedSlide = {
                                      ...tempSlide,
                                      title: result.title,
                                      content: result.content
                                    };
                                    
                                    setTempSlide(updatedSlide);
                                  }
                                  
                                  setHasUnsavedChanges(true);
                                  setShowToast({
                                    message: language === 'ko' ? '슬라이드가 재생성되었습니다.' : 'Slide regenerated.',
                                    type: 'success'
                                  });
                                  setShowPromptPanel(false);
                                } catch (error) {
                                  console.error('Error regenerating slide:', error);
                                  setShowToast({
                                    message: language === 'ko' ? '슬라이드 재생성에 실패했습니다.' : 'Failed to regenerate slide.',
                                    type: 'error'
                                  });
                                }
                              }}
                              onClose={() => setShowPromptPanel(false)}
                              language={language}
                              isDarkMode={isDarkMode}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* 우측 속성 패널 */}
                    {selectedElement && (
                      <div 
                        className="absolute z-[100]"
                        style={{
                          left: `${propertyPanelPosition.x}px`,
                          top: `${propertyPanelPosition.y}px`,
                        }}
                      >
                        <div className={`w-80 max-h-[80vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} rounded-lg border shadow-xl`}>
                          <div className="p-4 space-y-4">
                            <div 
                              className="flex items-center justify-between cursor-move"
                              onMouseDown={handlePanelMouseDown}
                            >
                              <h4 className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {selectedElement.type === 'text' || selectedElement.type === 'div' ? '텍스트 속성' : selectedElement.type === 'shape' ? '도형 속성' : selectedElement.type === 'image' ? '이미지 속성' : selectedElement.type === 'video' ? '동영상 속성' : selectedElement.type === 'list' ? '리스트 속성' : '차트 속성'}
                              </h4>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => deleteElement(selectedElement.id)}
                                  className={`p-1 rounded text-xs ${isDarkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'}`}
                                  title="삭제"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setSelectedElementId(undefined)}
                                  className={`p-1 rounded text-xs ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                  title="닫기"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>

                            {/* 우선순위 조절 */}
                            <div className="flex items-center justify-between">
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>우선순위</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateElement(selectedElement.id, { zIndex: Math.max(1, (selectedElement.zIndex || 1) - 1) })}
                                  className={`px-2 py-1 text-sm rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                >
                                  ↓
                                </button>
                                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} min-w-8 text-center`}>
                                  {selectedElement.zIndex || 1}
                                </span>
                                <button
                                  onClick={() => updateElement(selectedElement.id, { zIndex: (selectedElement.zIndex || 1) + 1 })}
                                  className={`px-2 py-1 text-sm rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                >
                                  ↑
                                </button>
                              </div>
                            </div>

                            {(selectedElement.type === 'text' || selectedElement.type === 'div') ? (
                              <div className="space-y-3">
                                {/* 텍스트 편집 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    텍스트 내용
                                  </label>
                                  <textarea
                                    value={selectedElement.content}
                                    onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                                    className={`w-full p-2 border rounded resize-none text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                                    rows={3}
                                    placeholder="텍스트 입력"
                                  />
                                </div>

                                {/* 폰트 설정 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      폰트
                                    </label>
                                    <select
                                      value={selectedElement.fontFamily || 'Noto Sans KR'}
                                      onChange={(e) => updateElement(selectedElement.id, { fontFamily: e.target.value })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                      <option value="'Noto Sans KR', sans-serif">기본 (Noto Sans KR)</option>
                                      <option value="'Pretendard', 'Noto Sans KR', sans-serif">모던 클린</option>
                                      <option value="'Noto Serif KR', serif">우아한 세리프</option>
                                      <option value="'Black Han Sans', sans-serif">임팩트 볼드</option>
                                      <option value="'Jua', sans-serif">귀여운 라운드</option>
                                      <option value="'Kalam', cursive">손글씨 스타일</option>
                                      <option value="'JetBrains Mono', monospace">테크 모노</option>
                                      <option value="'Crimson Text', serif">빈티지 클래식</option>
                                      <option value="'Orbitron', sans-serif">미래적</option>
                                      <option value="'Caveat', cursive">아티스틱 브러시</option>
                                      <option value="'Nanum Gothic', sans-serif">나눔고딕</option>
                                      <option value="'Nanum Myeongjo', serif">나눔명조</option>
                                      <option value="'NanumSquare', sans-serif">나눔스퀘어</option>
                                      <option value="'MaruBuri', serif">마루부리</option>
                                      <option value="Arial, sans-serif">Arial</option>
                                      <option value="Georgia, serif">Georgia</option>
                                      <option value="'Times New Roman', serif">Times New Roman</option>
                                      <option value="Verdana, sans-serif">Verdana</option>
                                      <option value="'Courier New', monospace">Courier New</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기
                                    </label>
                                    <input
                                      type="number"
                                      min="8"
                                      max="200"
                                      value={selectedElement.fontSize}
                                      onChange={(e) => updateElement(selectedElement.id, { fontSize: parseInt(e.target.value) || 16 })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      굵기
                                    </label>
                                    <select
                                      value={selectedElement.fontWeight || 'normal'}
                                      onChange={(e) => updateElement(selectedElement.id, { fontWeight: e.target.value })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                      <option value="normal">보통</option>
                                      <option value="bold">굵게</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      색상
                                    </label>
                                    <input
                                      type="color"
                                      value={selectedElement.color || '#000000'}
                                      onChange={(e) => updateElement(selectedElement.id, { color: e.target.value })}
                                      className="w-full h-8 rounded cursor-pointer border"
                                    />
                                  </div>
                                </div>

                                {/* 정렬 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    텍스트 정렬
                                  </label>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { textAlign: 'left' })}
                                      className={`flex-1 px-2 py-1 text-sm rounded ${selectedElement.textAlign === 'left' ? 'bg-blue-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                                    >
                                      좌측
                                    </button>
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { textAlign: 'center' })}
                                      className={`flex-1 px-2 py-1 text-sm rounded ${selectedElement.textAlign === 'center' ? 'bg-blue-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                                    >
                                      가운데
                                    </button>
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { textAlign: 'right' })}
                                      className={`flex-1 px-2 py-1 text-sm rounded ${selectedElement.textAlign === 'right' ? 'bg-blue-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                                    >
                                      우측
                                    </button>
                                  </div>
                                </div>

                                {/* 위치 및 크기 */}
                                <div className="space-y-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="number"
                                        step={selectedElement.x < 1 && selectedElement.y < 1 ? "0.01" : "1"}
                                        value={selectedElement.x < 1 && selectedElement.y < 1 ? Number(selectedElement.x.toFixed(2)) : Math.round(selectedElement.x)}
                                        onChange={(e) => {
                                          const isRatioBased = selectedElement.x < 1 && selectedElement.y < 1;
                                          const newValue = isRatioBased ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0;
                                          updateElement(selectedElement.id, { x: newValue });
                                        }}
                                        className={`flex-1 px-3 py-1.5 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        step={selectedElement.x < 1 && selectedElement.y < 1 ? "0.01" : "1"}
                                        value={selectedElement.x < 1 && selectedElement.y < 1 ? Number(selectedElement.y.toFixed(2)) : Math.round(selectedElement.y)}
                                        onChange={(e) => {
                                          const isRatioBased = selectedElement.x < 1 && selectedElement.y < 1;
                                          const newValue = isRatioBased ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0;
                                          updateElement(selectedElement.id, { y: newValue });
                                        }}
                                        className={`flex-1 px-3 py-1.5 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="number"
                                        step={selectedElement.width < 1 && selectedElement.height < 1 ? "0.01" : "1"}
                                        value={selectedElement.width < 1 && selectedElement.height < 1 ? Number(selectedElement.width.toFixed(2)) : Math.round(selectedElement.width)}
                                        onChange={(e) => {
                                          const isRatioBased = selectedElement.width < 1 && selectedElement.height < 1;
                                          const newValue = isRatioBased ? parseFloat(e.target.value) || 0.1 : parseInt(e.target.value) || 50;
                                          updateElement(selectedElement.id, { width: newValue });
                                        }}
                                        className={`flex-1 px-3 py-1.5 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        step={selectedElement.width < 1 && selectedElement.height < 1 ? "0.01" : "1"}
                                        value={selectedElement.width < 1 && selectedElement.height < 1 ? Number(selectedElement.height.toFixed(2)) : Math.round(selectedElement.height)}
                                        onChange={(e) => {
                                          const isRatioBased = selectedElement.width < 1 && selectedElement.height < 1;
                                          const newValue = isRatioBased ? parseFloat(e.target.value) || 0.1 : parseInt(e.target.value) || 30;
                                          updateElement(selectedElement.id, { height: newValue });
                                        }}
                                        className={`flex-1 px-3 py-1.5 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* 회전 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    회전 ({Math.round(selectedElement.rotation || 0)}°)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={selectedElement.rotation || 0}
                                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) })}
                                      className="flex-1 h-6"
                                    />
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                                      className={`p-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                      title="회전 초기화"
                                    >
                                      <RotateCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : selectedElement.type === 'shape' ? (
                              <div className="space-y-3">
                                {/* 색상 설정 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      채우기 색상
                                    </label>
                                    <input
                                      type="color"
                                      value={selectedElement.fillColor || '#3B82F6'}
                                      onChange={(e) => updateElement(selectedElement.id, { fillColor: e.target.value })}
                                      className="w-full h-8 rounded cursor-pointer border"
                                    />
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      테두리 색상
                                    </label>
                                    <input
                                      type="color"
                                      value={selectedElement.borderColor || '#1E40AF'}
                                      onChange={(e) => updateElement(selectedElement.id, { borderColor: e.target.value })}
                                      className="w-full h-8 rounded cursor-pointer border"
                                    />
                                  </div>
                                </div>

                                {/* 테두리 두께 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    테두리 두께 ({selectedElement.borderWidth || 2}px)
                                  </label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="10"
                                    value={selectedElement.borderWidth || 2}
                                    onChange={(e) => updateElement(selectedElement.id, { borderWidth: parseInt(e.target.value) })}
                                    className="w-full h-6"
                                  />
                                </div>

                                {/* 위치 및 크기 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.width)}
                                        onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.height)}
                                        onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 30 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* 회전 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    회전 ({Math.round(selectedElement.rotation || 0)}°)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={selectedElement.rotation || 0}
                                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) })}
                                      className="flex-1 h-6"
                                    />
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                                      className={`p-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                      title="회전 초기화"
                                    >
                                      <RotateCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : selectedElement.type === 'image' ? (
                              <div className="space-y-3">
                                {/* 이미지 변경 버튼 */}
                                <button
                                  onClick={() => handleImageUpload(selectedElement.id)}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                >
                                  <Upload className="w-4 h-4" />
                                  {t.changeImage || (language === 'ko' ? '이미지 변경' : 'Change Image')}
                                </button>

                                {/* 위치 및 크기 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.width)}
                                        onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.height)}
                                        onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 30 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* 회전 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    회전 ({Math.round(selectedElement.rotation || 0)}°)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={selectedElement.rotation || 0}
                                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) })}
                                      className="flex-1 h-6"
                                    />
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                                      className={`p-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                      title="회전 초기화"
                                    >
                                      <RotateCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : selectedElement.type === 'video' ? (
                              <div className="space-y-3">
                                {/* 동영상 변경 버튼 */}
                                <button
                                  onClick={() => handleVideoUpload(selectedElement.id)}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                >
                                  <Upload className="w-4 h-4" />
                                  {language === 'ko' ? '동영상 변경' : 'Change Video'}
                                </button>

                                {/* 위치 및 크기 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.width)}
                                        onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.height)}
                                        onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 30 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* 회전 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    회전 ({Math.round(selectedElement.rotation || 0)}°)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={selectedElement.rotation || 0}
                                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) })}
                                      className="flex-1 h-6"
                                    />
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                                      className={`p-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                      title="회전 초기화"
                                    >
                                      <RotateCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : selectedElement.type === 'list' ? (
                              <div className="space-y-3">
                                {/* 리스트 내용 편집 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    리스트 내용
                                  </label>
                                  <textarea
                                    value={selectedElement.content}
                                    onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded text-sm h-32 resize-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    placeholder="각 줄마다 하나의 리스트 항목을 입력하세요"
                                  />
                                </div>

                                {/* 폰트 설정 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      폰트 크기
                                    </label>
                                    <input
                                      type="number"
                                      value={selectedElement.fontSize}
                                      onChange={(e) => updateElement(selectedElement.id, { fontSize: parseInt(e.target.value) || 16 })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                      min="8"
                                      max="72"
                                    />
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      폰트 굵기
                                    </label>
                                    <select
                                      value={selectedElement.fontWeight || 'normal'}
                                      onChange={(e) => updateElement(selectedElement.id, { fontWeight: e.target.value })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                      <option value="normal">보통</option>
                                      <option value="bold">굵게</option>
                                    </select>
                                  </div>
                                </div>

                                {/* 색상 및 정렬 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      텍스트 색상
                                    </label>
                                    <input
                                      type="color"
                                      value={selectedElement.color || '#000000'}
                                      onChange={(e) => updateElement(selectedElement.id, { color: e.target.value })}
                                      className="w-full h-8 rounded cursor-pointer border"
                                    />
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      정렬
                                    </label>
                                    <select
                                      value={selectedElement.textAlign || 'left'}
                                      onChange={(e) => updateElement(selectedElement.id, { textAlign: e.target.value as 'left' | 'center' | 'right' })}
                                      className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                      <option value="left">좌측</option>
                                      <option value="center">가운데</option>
                                      <option value="right">우측</option>
                                    </select>
                                  </div>
                                </div>

                                {/* 위치 및 크기 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.width)}
                                        onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.height)}
                                        onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 30 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* 회전 */}
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    회전 ({Math.round(selectedElement.rotation || 0)}°)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={selectedElement.rotation || 0}
                                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) })}
                                      className="flex-1 h-6"
                                    />
                                    <button
                                      onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                                      className={`p-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                      title="회전 초기화"
                                    >
                                      <RotateCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {/* 기타 요소 타입의 기본 속성 */}
                                <div className="text-sm text-gray-500">
                                  {selectedElement.type} 타입 요소
                                </div>
                                
                                {/* 위치 및 크기 */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      위치 (X, Y)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="X"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="Y"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      크기 (W, H)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.width)}
                                        onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="W"
                                      />
                                      <input
                                        type="number"
                                        value={Math.round(selectedElement.height)}
                                        onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 30 })}
                                        className={`w-20 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        placeholder="H"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 공통 속성: 투명도 및 표시 옵션 */}
                            <div className="space-y-3 border-t pt-3">
                              {/* 배경 투명도 */}
                              <div>
                                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  배경 투명도 ({Math.round((selectedElement.backgroundOpacity !== undefined ? selectedElement.backgroundOpacity : 1) * 100)}%)
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={Math.round((selectedElement.backgroundOpacity !== undefined ? selectedElement.backgroundOpacity : 1) * 100)}
                                  onChange={(e) => updateElement(selectedElement.id, { backgroundOpacity: parseInt(e.target.value) / 100 })}
                                  className="w-full h-6"
                                />
                              </div>

                              {/* 체크박스 옵션들 */}
                              <div className="space-y-2">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedElement.hideBackground || false}
                                    onChange={(e) => updateElement(selectedElement.id, { hideBackground: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    배경 숨기기
                                  </span>
                                </label>

                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedElement.leftBorderHighlight || false}
                                    onChange={(e) => updateElement(selectedElement.id, { 
                                      leftBorderHighlight: e.target.checked,
                                      leftBorderColor: e.target.checked ? theme.secondary : undefined,
                                      leftBorderWidth: e.target.checked ? 4 : undefined
                                    })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    좌측 테두리 강조 (테마 색상)
                                  </span>
                                </label>
                              </div>
                            </div>

                            {/* 요소 배치 버튼 */}
                            <div>
                              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                요소 배치
                              </label>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => applyElementAlignment('left')}
                                  className={`flex-1 px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded`}
                                  title="좌측 정렬"
                                >
                                  <AlignLeft className="w-4 h-4 mx-auto" />
                                </button>
                                <button
                                  onClick={() => applyElementAlignment('center')}
                                  className={`flex-1 px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded`}
                                  title="가운데 정렬"
                                >
                                  <AlignCenter className="w-4 h-4 mx-auto" />
                                </button>
                                <button
                                  onClick={() => applyElementAlignment('right')}
                                  className={`flex-1 px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded`}
                                  title="우측 정렬"
                                >
                                  <AlignRight className="w-4 h-4 mx-auto" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* 선택되지 않은 슬라이드는 미리보기만 표시 */
                <div style={{ display: 'inline-block', padding: '20px 0' }}>
                  <div
                    className="relative cursor-pointer"
                    data-slide-id={slide.id}
                    onClick={() => onSlideSelect(slide.id)}
                    ref={containerRef}
                    style={{
                      position: 'relative',
                      width: `${editModeScaling.actualWidth}px`,
                      height: `${editModeScaling.actualHeight}px`,
                      padding: 0,
                      margin: 0,
                      cursor: 'crosshair',
                      overflow: 'hidden',
                      display: 'inline-block',
                      boxSizing: 'border-box'
                    }}
                  >
                  <SlideRenderer
                    slide={slide}
                    aspectRatio={aspectRatio}
                    theme={theme}
                    themeFont={themeFont}
                    borderStyle={slideBorderStyle}
                    isEditable={false}
                    language={language}
                    scale={editModeScaling.scale}
                    containerWidth={editModeScaling.actualWidth}
                    containerHeight={editModeScaling.actualHeight}
                    isDarkMode={isDarkMode}
                    disableTransformScale={true}
                  />
                  </div>
                </div>
              )}

            </div>

            {index < slides.length - 1 && (
              <div className="py-6">
                <div style={{ display: 'inline-block', paddingLeft: '20px' }}>
                  <div 
                    style={{ 
                      width: `${editModeScaling.actualWidth}px`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {/* Add button */}
                    <button
                      onClick={() => onAddSlide(index)}
                      className={`group relative flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-full transition-all duration-200 ${
                        isDarkMode 
                          ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-600 hover:border-blue-500' 
                          : 'bg-white hover:bg-blue-50 text-gray-600 hover:text-blue-600 border border-gray-300 hover:border-blue-300 shadow-sm hover:shadow-md'
                      } hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                      style={{ width: '200px', justifyContent: 'center', display: 'flex' }}
                    >
                      <Plus className="w-3 h-3 group-hover:rotate-180 transition-transform duration-300" />
                      <span className="hidden sm:inline">{t.addSlideHere}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
            
              {/* 슬라이드 제목과 저장 버튼 - 편집창 아래에 표시 */}
              {activeSlideId !== slide.id && (
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* 슬라이드 제목만 표시 (순서 조절 버튼은 상단 툴바로 이동) */}
                    {showTypingEffect && index < slidesDisplayCount ? (
                      <TypingEffect
                        text={`${t.slide || (language === 'ko' ? '슬라이드' : 'Slide')} ${index + 1}`}
                        speed={50}
                        startDelay={index * 500}
                        className={`text-sm font-medium break-words ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
                      />
                    ) : (
                      <span className={`text-sm font-medium break-words ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {t.slide || (language === 'ko' ? '슬라이드' : 'Slide')} {index + 1}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => exportSlideAsImage(slide)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    {t.saveImage}
                  </button>
                </div>
              )}
          </React.Fragment>
        ))}
        
        
        {/* 최하단 슬라이드 추가 버튼 */}
        {slides.length > 0 && (
          <div className="py-6 mb-20">
            <div style={{ display: 'inline-block', paddingLeft: '20px' }}>
              <div 
                style={{ 
                  width: `${editModeScaling.actualWidth}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Add button */}
                <button
                  onClick={() => onAddSlide()}
                  className={`group relative flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-full transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-600 hover:border-blue-500' 
                      : 'bg-white hover:bg-blue-50 text-gray-600 hover:text-blue-600 border border-gray-300 hover:border-blue-300 shadow-sm hover:shadow-md'
                  } hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  style={{ width: '200px', justifyContent: 'center', display: 'flex' }}
                >
                  <Plus className="w-3 h-3 group-hover:rotate-180 transition-transform duration-300" />
                  <span className="hidden sm:inline">{t.addSlide}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {/* 마지막 슬라이드를 위한 추가 여백 */}
        <div style={{ height: '120px' }} aria-hidden="true" />
      </div>

      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const blobUrl = URL.createObjectURL(file);
            
            if (pendingElement?.type === 'image') {
              // Create new image element
              const dimensions = getScaledElementDimensions('image');
              const newElement: SlideElement = {
                id: `element-${Date.now()}`,
                type: 'image',
                content: blobUrl,
                contentBlob: file, // Blob 객체 저장
                x: dimensions.x,
                y: dimensions.y,
                width: dimensions.width,
                height: dimensions.height,
                fontSize: 16,
                fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                color: '#000000',
                fontWeight: 'normal',
                textAlign: 'left',
                zIndex: (tempSlide?.elements?.length || 0) + 1,
              };
              setTempSlide(prev => prev ? {
                ...prev,
                elements: [...(prev.elements || []), newElement],
              } : null);
              setHasUnsavedChanges(true);
              setSelectedElementId(newElement.id);
            }
          }
        }}
      />

      {/* 텍스트 타입 선택 모달 */}
      {showTextTypeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} rounded-lg border p-6 max-w-md w-full mx-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                텍스트 타입 선택
              </h3>
              <button
                onClick={() => setShowTextTypeSelector(false)}
                className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  addNewElement('text', undefined, 'title');
                  setShowTextTypeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <Type className="w-8 h-8 text-blue-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>제목</span>
              </button>
              <button
                onClick={() => {
                  addNewElement('text', undefined, 'content');
                  setShowTextTypeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <MessageSquare className="w-8 h-8 text-green-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>내용 (목록)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 도형 선택 모달 */}
      {showShapeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} rounded-lg border p-6 max-w-md w-full mx-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                도형 선택
              </h3>
              <button
                onClick={() => setShowShapeSelector(false)}
                className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => {
                  const dimensions = getScaledElementDimensions('shape');
                  const newElement: SlideElement = {
                    id: `element-${Date.now()}`,
                    type: 'shape',
                    content: 'square',
                    shapeType: 'rectangle',
                    x: dimensions.x,
                    y: dimensions.y,
                    width: dimensions.width,
                    height: dimensions.height,
                    fontSize: 16,
                    fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                    color: '#3B82F6',
                    backgroundColor: '#3B82F6',
                    fontWeight: 'normal',
                    textAlign: 'left',
                    zIndex: (tempSlide?.elements?.length || 0) + 1,
                  };
                  addElementWithGridSupport(newElement);
                  setShowShapeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <Square className="w-8 h-8 text-blue-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>사각형</span>
              </button>
              <button
                onClick={() => {
                  const dimensions = getScaledElementDimensions('shape');
                  const newElement: SlideElement = {
                    id: `element-${Date.now()}`,
                    type: 'shape',
                    content: 'circle',
                    shapeType: 'circle',
                    x: dimensions.x,
                    y: dimensions.y,
                    width: dimensions.width,
                    height: dimensions.height,
                    fontSize: 16,
                    fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                    color: '#10B981',
                    backgroundColor: '#10B981',
                    fontWeight: 'normal',
                    textAlign: 'left',
                    zIndex: (tempSlide?.elements?.length || 0) + 1,
                  };
                  addElementWithGridSupport(newElement);
                  setShowShapeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <Circle className="w-8 h-8 text-green-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>원형</span>
              </button>
              <button
                onClick={() => {
                  const dimensions = getScaledElementDimensions('shape');
                  const newElement: SlideElement = {
                    id: `element-${Date.now()}`,
                    type: 'shape',
                    content: 'triangle',
                    shapeType: 'triangle',
                    x: dimensions.x,
                    y: dimensions.y,
                    width: dimensions.width,
                    height: dimensions.height,
                    fontSize: 16,
                    fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                    color: '#F59E0B',
                    backgroundColor: '#F59E0B',
                    fontWeight: 'normal',
                    textAlign: 'left',
                    zIndex: (tempSlide?.elements?.length || 0) + 1,
                  };
                  addElementWithGridSupport(newElement);
                  setShowShapeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <Triangle className="w-8 h-8 text-yellow-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>삼각형</span>
              </button>
              <button
                onClick={() => {
                  const dimensions = getScaledElementDimensions('shape');
                  const newElement: SlideElement = {
                    id: `element-${Date.now()}`,
                    type: 'shape',
                    content: 'star',
                    shapeType: 'star',
                    x: dimensions.x,
                    y: dimensions.y,
                    width: dimensions.width,
                    height: dimensions.height,
                    fontSize: 16,
                    fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                    color: '#EF4444',
                    backgroundColor: '#EF4444',
                    fontWeight: 'normal',
                    textAlign: 'left',
                    zIndex: (tempSlide?.elements?.length || 0) + 1,
                  };
                  addElementWithGridSupport(newElement);
                  setShowShapeSelector(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-opacity-10 transition-colors ${
                  isDarkMode ? 'border-gray-600 hover:bg-white' : 'border-gray-300 hover:bg-gray-900'
                }`}
              >
                <Star className="w-8 h-8 text-red-600" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>별</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 레이어 패널 - Desktop/Mobile 반응형 */}
      {showLayerPanel && (tempSlide || currentSlide) && (
        <>
          {/* Desktop: 기존 방식 */}
          <div className="hidden md:block">
            <LayerPanel
              slide={tempSlide || currentSlide!}
              isOpen={showLayerPanel}
              onClose={() => setShowLayerPanel(false)}
              onElementSelect={(elementId: string) => handleElementSelect(elementId, false)}
              selectedElementId={selectedElementId}
              onElementUpdate={updateElement}
              isDarkMode={isDarkMode}
            />
          </div>
          
          {/* Mobile: Modal 방식 */}
          <div className="md:hidden fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className={`w-full max-w-md max-h-[80vh] rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 overflow-y-auto m-4`}>
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ko' ? '레이어' : 'Layers'}
                </h3>
                <button
                  onClick={() => setShowLayerPanel(false)}
                  className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <LayerPanel
                slide={tempSlide || currentSlide!}
                isOpen={showLayerPanel}
                onClose={() => setShowLayerPanel(false)}
                onElementSelect={(elementId: string) => handleElementSelect(elementId, false)}
                selectedElementId={selectedElementId}
                onElementUpdate={updateElement}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </>
      )}

      {/* Toast 메시지 */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}

      {/* 삭제 확인 다이얼로그 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`p-6 rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <h3 className="text-lg font-semibold mb-4">{t.confirmDelete || '요소를 삭제하시겠습니까?'}</h3>
            <p className="mb-6 text-sm">{t.deleteWarning || '이 작업은 되돌릴 수 없습니다.'}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {t.cancel || '취소'}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                {t.delete || '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 키보드 단축키 힌트 */}
      {selectedElementId && showKeyboardHints && !hideKeyboardHintsPermanently && (
        <div className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 ${
          isDarkMode ? 'bg-gray-800/90' : 'bg-white/90'
        } backdrop-blur-sm rounded-lg border ${
          isDarkMode ? 'border-gray-700' : 'border-gray-300'
        } shadow-lg p-3 transition-opacity duration-300`}>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-xs">
              <div className="flex items-center gap-2">
                <kbd className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  ↑↓←→
                </kbd>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {language === 'ko' ? '이동' : 'Move'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  Shift + ↑↓←→
                </kbd>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {language === 'ko' ? '크기 조절' : 'Resize'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  Delete
                </kbd>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {language === 'ko' ? '삭제' : 'Delete'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  Ctrl+C/V
                </kbd>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {language === 'ko' ? '복사/붙여넣기' : 'Copy/Paste'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  Shift+Click
                </kbd>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {language === 'ko' ? '다중 선택' : 'Multi-select'}
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                setShowKeyboardHints(false);
                setHideKeyboardHintsPermanently(true);
                // Save to IndexedDB
                indexedDBService.saveUserPreferences({
                  collapsedSections,
                  propertyPanelPosition,
                  hideKeyboardHints: true
                }).catch(error => {
                  console.warn('Failed to save keyboard hints preference:', error);
                });
              }}
              className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700`}
              title={language === 'ko' ? '숨기기' : 'Hide'}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* 그룹 제어 버튼 */}
      {selectedElementIds.length > 1 && (
        <div className={`fixed bottom-16 left-1/2 transform -translate-x-1/2 z-50 ${
          isDarkMode ? 'bg-gray-800/90' : 'bg-white/90'
        } backdrop-blur-sm rounded-lg border ${
          isDarkMode ? 'border-gray-700' : 'border-gray-300'
        } shadow-lg p-3`}>
          <div className="flex items-center gap-3">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {selectedElementIds.length}개 선택됨
            </span>
            <button
              onClick={createGroup}
              className={`px-3 py-1 text-xs rounded-lg ${
                isDarkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } transition-colors`}
            >
              그룹핑
            </button>
          </div>
        </div>
      )}

      {/* 그룹 해제 버튼 */}
      {selectedElementId && tempSlide?.elements?.find(el => el.id === selectedElementId)?.groupId && (
        <div className={`fixed bottom-16 left-1/2 transform -translate-x-1/2 z-50 ${
          isDarkMode ? 'bg-gray-800/90' : 'bg-white/90'
        } backdrop-blur-sm rounded-lg border ${
          isDarkMode ? 'border-gray-700' : 'border-gray-300'
        } shadow-lg p-3`}>
          <button
            onClick={() => {
              const element = tempSlide?.elements?.find(el => el.id === selectedElementId);
              if (element?.groupId) {
                ungroupElements(element.groupId);
              }
            }}
            className={`px-3 py-1 text-xs rounded-lg ${
              isDarkMode 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            } transition-colors`}
          >
            그룹 해제
          </button>
        </div>
      )}

      {/* 키보드 힌트 토글 버튼 */}
      {selectedElementId && !showKeyboardHints && (
        <button
          onClick={() => {
            setShowKeyboardHints(true);
            setHideKeyboardHintsPermanently(false);
            // Save to IndexedDB
            indexedDBService.saveUserPreferences({
              collapsedSections,
              propertyPanelPosition,
              hideKeyboardHints: false
            }).catch(error => {
              console.warn('Failed to save keyboard hints preference:', error);
            });
          }}
          className={`fixed bottom-4 right-4 z-50 p-2 ${
            isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } rounded-full border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-300'
          } shadow-lg`}
          title={language === 'ko' ? '키보드 단축키 보기' : 'Show keyboard shortcuts'}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      )}

      {/* Pixabay Search Modal */}
      {showPixabaySearch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`w-full max-w-4xl h-3/4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl overflow-hidden`}>
            <div className="h-full">
              <BackgroundController
                currentSeed={tempSlide?.backgroundSeed || 'default'}
                blur={tempSlide?.backgroundBlur || 2}
                grayscale={tempSlide?.backgroundGrayscale || false}
                width={aspectRatio.width}
                height={aspectRatio.height}
                backgroundType={tempSlide?.backgroundType || 'image'}
                currentPattern={tempSlide?.backgroundPattern || 'none'}
                animatedEffect={tempSlide?.animatedBackgroundEffect || 'fog'}
                animatedOptions={tempSlide?.animatedBackgroundOptions || {}}
                previousSlideBackgroundSeed={(() => {
                  const currentIndex = slides.findIndex(s => s.id === activeSlideId);
                  if (currentIndex > 0) {
                    return slides[currentIndex - 1].backgroundSeed;
                  }
                  return undefined;
                })()}
                onSeedChange={handleBackgroundSeedChange}
                onBlurChange={handleBackgroundBlurChange}
                onGrayscaleChange={handleBackgroundGrayscaleChange}
                onBackgroundTypeChange={(type) => {
                  if (!tempSlide) {return;}
                  const updatedSlide = { ...tempSlide, backgroundType: type };
                  
                  // 애니메이션 타입으로 변경할 때 기본 효과 설정
                  if (type === 'animated' && !tempSlide.animatedBackgroundEffect) {
                    updatedSlide.animatedBackgroundEffect = 'fog';
                    updatedSlide.animatedBackgroundOptions = {
                      backgroundColor: theme.primary,
                      color1: theme.secondary,
                      color2: theme.tertiary,
                      scale: 1,
                      speed: 1,
                    };
                  }
                  
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                }}
                onBackgroundChange={(background) => {
                  if (!tempSlide) {return;}
                  const updatedSlide = {
                    ...tempSlide,
                    backgroundImage: background,
                    backgroundType: 'image'
                  };
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                  setShowPixabaySearch(false);
                }}
                onBackgroundBlobChange={(blob: Blob) => {
                  if (!tempSlide) return;
                  const updatedSlide = { ...tempSlide, backgroundImageBlob: blob };
                  setTempSlide(updatedSlide);
                  setHasUnsavedChanges(true);
                }}
                onPatternChange={(pattern) => {
                  if (!tempSlide) {return;}
                  setTempSlide(prev => prev ? { ...prev, backgroundPattern: pattern } : null);
                  setHasUnsavedChanges(true);
                }}
                onAnimatedEffectChange={(effect) => {
                  if (!tempSlide) {return;}
                  setTempSlide(prev => prev ? { ...prev, animatedBackgroundEffect: effect } : null);
                  setHasUnsavedChanges(true);
                }}
                onAnimatedOptionsChange={(options) => {
                  if (!tempSlide) {return;}
                  setTempSlide(prev => prev ? { ...prev, animatedBackgroundOptions: options } : null);
                  setHasUnsavedChanges(true);
                }}
                onImageElementAdd={(imageUrl) => {
                  if (!tempSlide) {return;}
                  const dimensions = getScaledElementDimensions('image');
                  const newElement: SlideElement = {
                    id: `element-${Date.now()}`,
                    type: 'image',
                    content: imageUrl,
                    x: dimensions.x,
                    y: dimensions.y,
                    width: dimensions.width,
                    height: dimensions.height,
                    fontSize: 16,
                    fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                    color: '#000000',
                    fontWeight: 'normal',
                    textAlign: 'left',
                    zIndex: (tempSlide?.elements?.length || 0) + 1,
                  };
                  addElementWithGridSupport(newElement);
                  setShowPixabaySearch(false);
                }}
                isDarkMode={isDarkMode}
                language={language}
              />
              <button
                onClick={() => setShowPixabaySearch(false)}
                className={`absolute top-4 right-4 p-2 rounded-full ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
                } shadow-lg`}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

